import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, Star, MapPin, Clock, Camera, Zap, Heart,
  TrendingUp, Package, Truck, Shield, Award, Users, Instagram, Twitter,
  Store, Share, Facebook, Calendar, Play, Pause, Volume2, VolumeX, Maximize, Mail, Phone,
  CheckCircle, Eye, EyeOff, Move, ZoomIn, Grid, Plus, Minus, ThumbsUp, MessageSquare,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import FeaturedItem from '../../components/shop/FeaturedItem';
import { CategoryScrollableGrid, HeroScrollContainer } from '../../components/shop/CategoryGridLayouts';
import { compressProductImage, validateImageFile, readFileAsDataURL } from '../../utils/mediaCompression';

// Fix Leaflet default marker icons under webpack (same fix as TreasureMap.js)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ─── YOUTUBE IFRAME API LOADER (shared singleton) ───────────────────────────
let ytApiPromise = null;
const loadYouTubeIframeAPI = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const prevCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prevCallback === 'function') prevCallback();
      resolve(window.YT);
    };
    if (!document.getElementById('youtube-iframe-api')) {
      const tag = document.createElement('script');
      tag.id = 'youtube-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  });
  return ytApiPromise;
};

// ─── GLOBAL STYLES (injected once) ──────────────────────────────────────────
const HPT_GLOBAL_CSS = `
  @keyframes hpt-fadeUp   { from{opacity:0;transform:translateY(36px) scale(.98)} to{opacity:1;transform:translateY(0) scale(1)} }
  @keyframes hpt-shimmer  { 0%{background-position:-400% center} 100%{background-position:400% center} }
  @keyframes hpt-slowZoom { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }
  @keyframes hpt-heroIn   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
  @keyframes hpt-pulse    { 0%,100%{opacity:1} 50%{opacity:.7} }
  @keyframes hpt-spin     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

  .hpt-card { transition: transform .35s cubic-bezier(.34,1.56,.64,1), box-shadow .35s ease, border-color .3s ease; }
  .hpt-card:hover { transform: translateY(-7px) scale(1.025); }
  .hpt-card:hover .hpt-img-inner { transform: scale(1.09); }
  .hpt-img-inner { transition: transform .5s cubic-bezier(.4,0,.2,1); will-change:transform; }

  .hpt-chip-scroll {
    display: flex; gap: 1rem;
    overflow-x: auto; scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding: .25rem 0 1.25rem; scrollbar-width: none;
  }
  .hpt-chip-scroll::-webkit-scrollbar { display: none; }

  /* Desktop: grid / Mobile: horizontal scroll */
  .hpt-grid  { display: grid; }
  .hpt-mscroll { display: none !important; }
  @media (max-width: 768px) {
    .hpt-grid   { display: none !important; }
    .hpt-mscroll{ display: flex !important; }
  }

  /* ── DESKTOP LAYOUT SYSTEM (≥769px only) ─────────────────────────────── */
  @media (min-width: 769px) {
    /* Section body: generous side padding + centered max-width */
    .hpt-sp  { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); }
    .hpt-wrap { max-width: 1320px; margin: 0 auto; }

    /* FAQ: 2-column accordion grid */
    .hpt-faq-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 0.25rem 2.5rem; align-items: start; }

    /* Reviews: big rating sidebar + cards grid */
    .hpt-rev-side { display: grid; grid-template-columns: 220px 1fr; gap: 3rem; align-items: start; }

    /* Map: info sidebar left, map right */
    .hpt-map-side { display: grid; grid-template-columns: 280px 1fr; gap: 2rem; align-items: start; }

    /* Gallery: main viewer + vertical thumbnail sidebar */
    .hpt-gal-side { display: grid; grid-template-columns: 1fr 96px; gap: 1rem; align-items: start; }
    .hpt-gal-vthumbs { display: flex !important; flex-direction: column; gap: 0.5rem;
      max-height: clamp(280px,48vw,560px); overflow-y: auto; scrollbar-width: none; }
    .hpt-gal-vthumbs::-webkit-scrollbar { display: none; }

    /* Services: more columns, bigger tiles */
    .hpt-svc-dtiles { grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)) !important; gap: 1.75rem !important; }

    /* Contact: 3-column on desktop */
    .hpt-contact-3col { grid-template-columns: repeat(3,1fr) !important; gap: 2rem !important; max-width: none !important; justify-content: stretch !important; }

    /* Calendar: 3-column cards */
    .hpt-cal-3col { grid-template-columns: repeat(3,1fr) !important; gap: 1.5rem !important; }

    /* Section heading: left-aligned on desktop for non-hero sections */
    .hpt-head-left { text-align: left !important; }
    .hpt-head-left .hpt-accent-bar { margin: .4rem 0 0 !important; }

    /* Text block: left accent border on desktop */
    .hpt-textblock-desk {
      padding-left: 1.75rem;
      border-left: 3px solid;
      border-image: linear-gradient(180deg, var(--hpt-accent, #6366f1), transparent) 1;
    }
  }
`;

let _hptInjected = false;
const useGlobalStyles = () => {
  useEffect(() => {
    if (_hptInjected || typeof document === 'undefined') return;
    const el = document.createElement('style');
    el.setAttribute('data-hpt', '1');
    el.textContent = HPT_GLOBAL_CSS;
    document.head.appendChild(el);
    _hptInjected = true;
  }, []);
};

// ─── SECTION WRAPPER ─────────────────────────────────────────────────────────
const SectionWrapper = ({ children, noPadding = false, delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useGlobalStyles();

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.05, rootMargin: '0px 0px -20px 0px' }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} style={{
      position: 'relative',
      padding: noPadding ? '0' : 'clamp(.5rem,1.5vw,1rem) 0',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0) scale(1)' : 'translateY(36px) scale(.98)',
      transition: `opacity .7s cubic-bezier(.4,0,.2,1) ${delay}s, transform .7s cubic-bezier(.34,1.56,.64,1) ${delay}s`,
      willChange: 'opacity,transform',
    }}>
      {children}
    </div>
  );
};


const SectionHead = ({ title, accent, text, editable, config, onUpdate, placeholder, alignLeft }) => (
  <div style={{ marginBottom: '1.25rem', textAlign: alignLeft ? undefined : 'center' }}
    className={alignLeft ? 'hpt-head-left' : ''}>
    {editable ? (
      <input type="text" value={title || ''} placeholder={placeholder || 'Section title'}
        onChange={e => onUpdate({ ...config, title: e.target.value })}
        style={{ fontSize:'clamp(1.1rem,2.8vw,1.8rem)', fontWeight:'800', background:'transparent',
          border:'none', color:text, outline:'none', width:'100%', letterSpacing:'-0.025em', textAlign: alignLeft ? undefined : 'center' }} />
    ) : (
      <h2 style={{ fontSize:'clamp(1.1rem,2.8vw,1.8rem)', fontWeight:'800', margin:0,
        letterSpacing:'-0.025em', color:text }}>{title || 'Section'}</h2>
    )}
    <div className="hpt-accent-bar" style={{ height:'3px', width:'2.25rem',
      background:`linear-gradient(90deg,${accent},${accent}40)`,
      borderRadius:'2px', margin: alignLeft ? '.4rem 0 0' : '.4rem auto 0' }} />
  </div>
);


// ─── TEMPLATE FRAMES ──────────────────────────────────────────────────────────
const StreetwearFrame = ({ children, theme }) => (
  <div style={{ position:'relative', overflow:'hidden', borderRadius:'8px', padding:'1px',
    background:`linear-gradient(180deg,${theme?.colors?.accent}30,${theme?.colors?.accent}10,${theme?.colors?.accent}30)` }}>
    <div style={{ position:'relative', zIndex:1, background:theme?.colors?.background, borderRadius:'7px' }}>
      {children}
    </div>
  </div>
);

const OrganizationFrame = ({ children, theme }) => (
  <div style={{ borderRadius:'16px', overflow:'hidden', border:`1px solid ${theme?.colors?.accent}15`,
    background:`linear-gradient(180deg,${theme?.colors?.surface}08 0%,transparent 100%)`, position:'relative' }}>
    <div style={{ height:'2px', background:`linear-gradient(90deg,transparent,${theme?.colors?.accent}60,transparent)`,
      position:'absolute', top:0, left:'20%', right:'20%', zIndex:2 }} />
    <div style={{ position:'relative', zIndex:1 }}>{children}</div>
  </div>
);

const TechFrame = ({ children, theme }) => (
  <div style={{ borderRadius:'12px', overflow:'hidden', border:`1px solid ${theme?.colors?.accent}20`,
    background:`radial-gradient(circle at 50% 0%,${theme?.colors?.accent}08 0%,transparent 50%)`, position:'relative' }}>
    <div style={{ position:'absolute', top:0, left:0, width:'30px', height:'30px',
      borderTop:`2px solid ${theme?.colors?.accent}40`, borderLeft:`2px solid ${theme?.colors?.accent}40`,
      borderTopLeftRadius:'12px', pointerEvents:'none' }} />
    <div style={{ position:'absolute', top:0, right:0, width:'30px', height:'30px',
      borderTop:`2px solid ${theme?.colors?.accent}40`, borderRight:`2px solid ${theme?.colors?.accent}40`,
      borderTopRightRadius:'12px', pointerEvents:'none' }} />
    <div style={{ position:'relative', zIndex:1 }}>{children}</div>
  </div>
);

const MinimalistFrame = ({ children, theme }) => (
  <div style={{ borderRadius:'24px', overflow:'hidden', position:'relative' }}>
    <div style={{ position:'absolute', inset:0, borderRadius:'24px',
      border:`1px solid ${theme?.colors?.accent}10`, pointerEvents:'none' }} />
    <div style={{ position:'relative', zIndex:1 }}>{children}</div>
  </div>
);

const LocalMarketFrame = ({ children, theme }) => (
  <div style={{ borderRadius:'16px', overflow:'hidden', position:'relative',
    border:`1px solid ${theme?.colors?.accent}20`,
    background:`linear-gradient(180deg,${theme?.colors?.accent}05 0%,transparent 30%,transparent 70%,${theme?.colors?.accent}05 100%)` }}>
    <div style={{ position:'absolute', top:0, left:0, right:0, height:'8px',
      background:`linear-gradient(90deg,transparent,${theme?.colors?.accent}15,transparent)`, zIndex:1 }} />
    <div style={{ position:'relative', zIndex:1, paddingTop:'8px' }}>{children}</div>
  </div>
);

// ─── DIVIDERS ─────────────────────────────────────────────────────────────────
const StreetwearDivider = ({ theme }) => (
  <div style={{ height:'80px', position:'relative', margin:'0' }}>
    <div style={{ position:'absolute', top:'50%', left:'10%', right:'10%', height:'1px',
      background:`linear-gradient(90deg,transparent,${theme?.colors?.accent}20,transparent)`,
      transform:'translateY(-50%)' }} />
  </div>
);
const OrganizationDivider = ({ theme }) => (
  <div style={{ height:'60px', background:`linear-gradient(180deg,transparent,${theme?.colors?.surface}05,transparent)` }} />
);
const TechDivider = ({ theme }) => (
  <div style={{ height:'90px', position:'relative', margin:'0',
    background:`radial-gradient(ellipse at center,${theme?.colors?.accent}05 0%,transparent 70%)` }}>
    <div style={{ position:'absolute', inset:0,
      backgroundImage:`linear-gradient(45deg,${theme?.colors?.accent}02 25%,transparent 25%),
        linear-gradient(-45deg,${theme?.colors?.accent}02 25%,transparent 25%),
        linear-gradient(45deg,transparent 75%,${theme?.colors?.accent}02 75%),
        linear-gradient(-45deg,transparent 75%,${theme?.colors?.accent}02 75%)`,
      backgroundSize:'20px 20px', backgroundPosition:'0 0,0 10px,10px -10px,-10px 0px', opacity:.3 }} />
  </div>
);
const MinimalistDivider = ({ theme }) => (
  <div style={{ height:'80px', position:'relative' }}>
    <div style={{ position:'absolute', top:'50%', left:'30%', right:'30%', height:'1px',
      background:`linear-gradient(90deg,transparent,${theme?.colors?.accent}08,transparent)`,
      transform:'translateY(-50%)' }} />
  </div>
);
const LocalMarketDivider = ({ theme }) => (
  <div style={{ height:'80px', position:'relative', margin:'0',
    background:`linear-gradient(180deg,transparent,${theme?.colors?.accent}04,transparent)` }}>
    <div style={{ position:'absolute', inset:0,
      backgroundImage:`radial-gradient(circle,${theme?.colors?.accent}08 1px,transparent 1px)`,
      backgroundSize:'30px 30px', opacity:.4 }} />
  </div>
);

// ─── TEXT BLOCK SECTION ───────────────────────────────────────────────────────
export const TextBlockSection = ({ config, theme, editable, onUpdate }) => {
  const accent = theme?.colors?.accent || '#6366f1';
  const text   = theme?.colors?.text   || '#fff';
  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) {
          .hpt-tb-outer { padding: clamp(2rem,3vw,3.5rem) clamp(3rem,6vw,6rem); }
          .hpt-tb-inner { max-width: 820px; }
        }
        @media (max-width: 768px) {
          .hpt-tb-outer { padding: clamp(1rem,2vw,2rem) clamp(.75rem,2vw,1.5rem); }
          .hpt-tb-inner { max-width: none; }
        }
      `}</style>
      <div className="hpt-tb-outer">
        <div className="hpt-tb-inner" style={{ margin:'0 auto' }}>
        {editable ? (
          <>
            <input type="text" value={config?.title || ''} placeholder="Section title (optional)"
              onChange={e => onUpdate({ ...config, title: e.target.value })}
              style={{ width:'100%', fontSize:'clamp(1.1rem,2.5vw,1.8rem)', fontWeight:'700', marginBottom:'.75rem',
                background:'transparent', border:'none', color:text, outline:'none',
                textAlign: config?.alignment || 'left' }} />
            <div style={{ marginBottom:'.75rem' }}>
              {['left','center','right'].map(a => (
                <button key={a} onClick={() => onUpdate({ ...config, alignment:a })}
                  style={{ background: config?.alignment===a ? accent : 'transparent',
                    color: config?.alignment===a ? '#fff' : text,
                    border:`1px solid ${accent}`, padding:'.35rem .8rem', marginRight:'.4rem',
                    borderRadius:'6px', cursor:'pointer', textTransform:'capitalize', fontSize:'.8rem' }}>
                  {a}
                </button>
              ))}
            </div>
            <textarea value={config?.content || ''} placeholder="Enter your content here..."
              onChange={e => onUpdate({ ...config, content: e.target.value })}
              style={{ width:'100%', fontSize:'clamp(.85rem,1.5vw,1rem)', lineHeight:1.65,
                background:'transparent', border:`1px solid ${accent}40`, borderRadius:'8px',
                color:text, padding:'.85rem', minHeight:'140px', resize:'vertical',
                textAlign: config?.alignment || 'left' }} />
          </>
        ) : (
          <div className="hpt-textblock-desk" style={{ '--hpt-accent': accent }}>
            {config?.title && (
              <h2 style={{ fontSize:'clamp(1.1rem,2.5vw,1.8rem)', fontWeight:'700',
                marginBottom:'.75rem', textAlign: config?.alignment || 'left', color:text }}>
                {config.title}
              </h2>
            )}
            <p style={{ fontSize:'clamp(.9rem,1.5vw,1.1rem)', lineHeight:1.8, opacity:.9,
              textAlign: config?.alignment || 'left', whiteSpace:'pre-line', color:text, margin:0 }}>
              {config?.content || 'Add your custom content here.'}
            </p>
          </div>
        )}
        </div>
      </div>
    </SectionWrapper>
  );
};

// ─── SERVICES GRID SECTION ────────────────────────────────────────────────────
export const ServicesGridSection = ({ config, theme, editable, onUpdate }) => {
  const services = config?.services || [];

  // Manually-curated tiles (not auto-populated) — an unfilled section would
  // otherwise show a bare title with nothing under it on the live public page.
  if (!editable && services.length === 0) return null;

  const accent   = theme?.colors?.accent   || '#6366f1';
  const surface  = theme?.colors?.surface  || '#1a1a2e';
  const text     = theme?.colors?.text     || '#fff';
  const bg       = theme?.colors?.background || '#0f0f1a';

  const iconMap = { Truck, Shield, Clock, Award, Heart, Zap, Users, Star };

  const handleAdd = () => onUpdate({ ...config, services:[...services,
    { id:Date.now().toString(), icon:'Star', title:'New Service', description:'Service description' }] });
  const handleEdit = (id, field, val) =>
    onUpdate({ ...config, services: services.map(s => s.id===id ? { ...s, [field]:val } : s) });
  const handleRemove = (id) =>
    onUpdate({ ...config, services: services.filter(s => s.id!==id) });

  // Shared tile renderer — mobile just tightens size/spacing, both are equally editable.
  const Tile = ({ s, mobile }) => {
    const Icon = iconMap[s.icon] || Star;
    return (
      <div style={{ textAlign:'center', padding: mobile ? '1.1rem 1rem' : 'clamp(1.5rem,2.5vw,2.25rem)',
        background:`${bg}80`, borderRadius:'16px', border:`1px solid ${accent}20`,
        position:'relative', ...(mobile ? { width:'180px', flexShrink:0, scrollSnapAlign:'start' } : {}) }}>
        {editable && (
          <>
            <select value={s.icon} onChange={e => handleEdit(s.id,'icon',e.target.value)}
              style={{ position:'absolute', top:'.5rem', left:'.5rem', padding:'.2rem',
                borderRadius:'4px', background:surface, color:text, border:`1px solid ${accent}40`, fontSize:'.7rem' }}>
              {Object.keys(iconMap).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button onClick={() => handleRemove(s.id)} style={{ position:'absolute', top:'.4rem', right:'.4rem',
              background:'transparent', border:'none', color:'#f87171', cursor:'pointer', fontSize:'1.3rem' }}>×</button>
          </>
        )}
        <div style={{ width: mobile ? '48px' : '72px', height: mobile ? '48px' : '72px', borderRadius:'50%',
          margin: mobile ? '0 auto .75rem' : '0 auto 1.25rem',
          background:`${accent}18`, display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow: mobile ? 'none' : `0 4px 20px ${accent}25` }}>
          <Icon size={mobile ? 22 : 32} color={accent} />
        </div>
        {editable ? (
          <>
            <input value={s.title} onChange={e => handleEdit(s.id,'title',e.target.value)}
              style={{ width:'100%', fontSize: mobile ? '.92rem' : '1.05rem', fontWeight:'700', marginBottom:'.4rem',
                background:'transparent', border:'none', color:text, textAlign:'center', outline:'none' }} />
            <textarea value={s.description} onChange={e => handleEdit(s.id,'description',e.target.value)}
              style={{ width:'100%', fontSize: mobile ? '.78rem' : '.85rem', background:'transparent',
                border:`1px solid ${accent}20`, borderRadius:'4px', color:text,
                opacity:.8, padding:'.4rem', textAlign:'center', minHeight:'48px', resize:'vertical' }} />
          </>
        ) : (
          <>
            <h3 style={{ fontSize: mobile ? '.92rem' : '1.05rem', fontWeight:'700', marginBottom: mobile ? '.35rem' : '.5rem', color:text, letterSpacing:'-.01em' }}>{s.title}</h3>
            <p style={{ fontSize: mobile ? '.78rem' : '.88rem', opacity:.75, lineHeight: mobile ? 1.5 : 1.6, color:text, margin:0 }}>{s.description}</p>
          </>
        )}
      </div>
    );
  };

  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) {
          .hpt-svc-outer { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); }
        }
        @media (max-width: 768px) {
          .hpt-svc-outer { padding: clamp(1rem,2vw,2rem) clamp(.75rem,2vw,1.5rem); }
        }
      `}</style>
      <div className="hpt-svc-outer">
        <div className="hpt-wrap">
        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between',
          marginBottom:'1.75rem', flexWrap:'wrap', gap:'.75rem' }}>
          <SectionHead title={config?.title} accent={accent} text={text} alignLeft
            editable={editable} config={config} onUpdate={onUpdate} placeholder="Services title" />
          {editable && (
            <button onClick={handleAdd} style={{ background:accent, color:'#fff', border:'none',
              padding:'.45rem 1rem', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'.82rem' }}>
              + Add
            </button>
          )}
        </div>

        {/* Desktop grid / Mobile scroll — both fully editable */}
        <div className="hpt-grid hpt-svc-dtiles" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(clamp(150px,25%,220px),1fr))', gap:'1.25rem' }}>
          {services.map(s => <Tile key={s.id} s={s} />)}
        </div>

        <div className="hpt-mscroll hpt-chip-scroll">
          {services.map(s => <Tile key={`m-${s.id}`} s={s} mobile />)}
        </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

// ─── CALENDAR EVENTS SECTION ──────────────────────────────────────────────────
export const CalendarEventsSection = ({ config, theme, editable, onUpdate }) => {
  const events  = config?.events || [];
  const accent  = theme?.colors?.accent   || '#6366f1';
  const surface = theme?.colors?.surface  || '#1a1a2e';
  const text    = theme?.colors?.text     || '#fff';

  const handleAdd = () => onUpdate({ ...config, events:[...events,
    { id:Date.now().toString(), title:'New Event', date:new Date().toISOString().split('T')[0], time:'10:00 AM', description:'Event description' }] });
  const handleEdit = (id, field, val) =>
    onUpdate({ ...config, events: events.map(e => e.id===id ? { ...e, [field]:val } : e) });
  const handleRemove = (id) =>
    onUpdate({ ...config, events: events.filter(e => e.id!==id) });

  const EventCard = ({ ev, mobile }) => (
    <div style={{ background:`${surface}90`, borderRadius:'16px', padding: mobile ? '1.1rem 1.2rem' : '1.5rem 1.75rem',
      border:`1px solid ${accent}28`, position:'relative',
      ...(mobile ? { width:'260px', flexShrink:0, scrollSnapAlign:'start' } : {}) }}>
      <div style={{ display:'flex', alignItems:'center', gap:'.5rem', marginBottom:'.65rem', color:accent }}>
        <Calendar size={18} />
        {editable ? (
          <input type="date" value={ev.date} onChange={e => handleEdit(ev.id,'date',e.target.value)}
            style={{ background:'transparent', border:'none', color:accent, fontSize:'.9rem', fontWeight:'600' }} />
        ) : (
          <span style={{ fontSize:'.88rem', fontWeight:'600' }}>
            {new Date(ev.date + 'T00:00:00').toLocaleDateString('en-US',{ month:'short', day:'numeric', year:'numeric' })}
          </span>
        )}
      </div>
      {editable ? (
        <>
          {mobile && <button onClick={() => handleRemove(ev.id)} style={{ position:'absolute', top:'.4rem', right:'.4rem',
            background:'transparent', border:'none', color:'#f87171', cursor:'pointer', fontSize:'1.2rem' }}>×</button>}
          <input value={ev.title} onChange={e => handleEdit(ev.id,'title',e.target.value)}
            style={{ width:'100%', fontSize:'1rem', fontWeight:'700', marginBottom:'.35rem',
              background:'transparent', border:'none', color:text, outline:'none' }} />
          <input value={ev.time} onChange={e => handleEdit(ev.id,'time',e.target.value)} placeholder="Time"
            style={{ width:'100%', fontSize:'.82rem', marginBottom:'.35rem',
              background:'transparent', border:'none', color:text, opacity:.7, outline:'none' }} />
          <textarea value={ev.description} onChange={e => handleEdit(ev.id,'description',e.target.value)}
            style={{ width:'100%', fontSize:'.82rem', background:'transparent',
              border:`1px solid ${accent}20`, borderRadius:'4px', color:text,
              opacity:.7, padding:'.4rem', minHeight:'52px', resize:'vertical' }} />
          {!mobile && <button onClick={() => handleRemove(ev.id)} style={{ position:'absolute', top:'.4rem', right:'.4rem',
            background:'transparent', border:'none', color:'#f87171', cursor:'pointer', fontSize:'1.2rem' }}>×</button>}
        </>
      ) : (
        <>
          <h3 style={{ fontSize:'1rem', fontWeight:'700', marginBottom:'.25rem', color:text }}>{ev.title}</h3>
          <div style={{ fontSize:'.82rem', opacity:.7, marginBottom:'.35rem', color:text, display:'flex', alignItems:'center', gap:'.3rem' }}>
            <Clock size={12} /> {ev.time}
          </div>
          <p style={{ fontSize:'.82rem', opacity:.75, lineHeight:1.5, color:text, margin:0 }}>{ev.description}</p>
        </>
      )}
    </div>
  );

  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) {
          .hpt-cal-outer { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); }
        }
        @media (max-width: 768px) {
          .hpt-cal-outer { padding: clamp(1rem,2vw,2rem) clamp(.75rem,2vw,1.5rem); }
        }
      `}</style>
      <div className="hpt-cal-outer">
        <div className="hpt-wrap">
        <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between',
          marginBottom:'1.75rem', flexWrap:'wrap', gap:'.75rem' }}>
          <SectionHead title={config?.title} accent={accent} text={text} alignLeft
            editable={editable} config={config} onUpdate={onUpdate} placeholder="Events title" />
          {editable && (
            <button onClick={handleAdd} style={{ background:accent, color:'#fff', border:'none',
              padding:'.45rem 1rem', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'.82rem' }}>
              + Add Event
            </button>
          )}
        </div>

        <div className="hpt-grid hpt-cal-3col" style={{ gridTemplateColumns:'repeat(auto-fill,minmax(clamp(200px,30%,300px),1fr))', gap:'1.1rem' }}>
          {events.length > 0 ? events.map(ev => <EventCard key={ev.id} ev={ev} />)
            : !editable && <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'3rem 2rem', opacity:.5, color:text }}>No upcoming events</div>}
        </div>
        <div className="hpt-mscroll hpt-chip-scroll">
          {events.map(ev => <EventCard key={`m-${ev.id}`} ev={ev} mobile />)}
        </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

// ─── CONTACT SECTION ─────────────────────────────────────────────────────────
// Example values older default-section generators used to save as real config
// data (instead of leaving the field blank for the input's placeholder to
// show). Treated as "not actually filled in" so the public page doesn't
// present them as the shop's real contact info.
const CONTACT_PLACEHOLDER_VALUES = {
  email: 'contact@example.com',
  phone: '(555) 123-4567',
  address: '123 Main Street\nCity, State 12345',
};

// Trim a raw address down to street, city, state only — strip zip and country.
const fmtAddress = (addr) => {
  if (!addr) return '';
  const parts = addr.replace(/\n/g, ', ').split(',').map(p => p.trim()).filter(Boolean);
  const out = parts.slice(0, 3);
  // Remove trailing 5- or 9-digit postal code from the last segment
  if (out.length) out[out.length - 1] = out[out.length - 1].replace(/\s+\d{5}(-\d{4})?$/, '').trim();
  return out.join(', ');
};

export const ContactSection = ({ shopData, config, theme, editable, onUpdate }) => {
  const accent  = theme?.colors?.accent   || '#6366f1';
  const surface = theme?.colors?.surface  || '#1a1a2e';
  const text    = theme?.colors?.text     || '#fff';
  const bg      = theme?.colors?.background || '#0f0f1a';

  // A field only counts as "filled in" if it's non-empty AND not one of the
  // old seeded example values — the owner never actually typed those in.
  const realField = (field) => {
    const v = config?.[field];
    return v && v !== CONTACT_PLACEHOLDER_VALUES[field] ? v : '';
  };

  // Auto-sync address from site settings (masterAddresses) when not manually overridden
  const autoAddress = (shopData?.masterAddresses || [])
    .filter(a => a?.address)
    .map(a => a.label ? `${a.label}: ${a.address}` : a.address)
    .join('\n');
  const effectiveAddress = realField('address') || autoAddress;

  const [links, setLinks] = useState(config?.socialLinks || { facebook:'', instagram:'', twitter:'', youtube:'', linkedin:'' });

  const updateLink = (p, v) => {
    const next = { ...links, [p]:v }; setLinks(next);
    onUpdate({ ...config, socialLinks:next });
  };
  const updateField = (f, v) => onUpdate({ ...config, [f]:v });

  const SocialIcon = ({ p }) => ({ facebook:<Facebook size={18}/>, instagram:<Instagram size={18}/>,
    twitter:<Twitter size={18}/>, youtube:<Play size={18}/>, linkedin:<Users size={18}/> }[p] || <Share size={18}/>);

  const platforms = ['facebook','instagram','twitter','youtube','linkedin'];

  return (
    <SectionWrapper>
      <div style={{ background:`linear-gradient(135deg,${surface} 0%,${bg} 100%)`,
        borderTop:`3px solid ${accent}`, overflow:'hidden' }}>
        <div style={{ padding:'clamp(2rem,5vw,4.5rem) clamp(2rem,6vw,6rem)', maxWidth:'1320px', margin:'0 auto' }}>

          {/* Header */}
          <div style={{ textAlign:'center', marginBottom:'clamp(2rem,4vw,3rem)' }}>
            {editable ? (
              <input type="text" value={config?.title||''} placeholder="Footer title"
                onChange={e => updateField('title',e.target.value)}
                style={{ fontSize:'clamp(1.4rem,3vw,2.2rem)', fontWeight:'700', background:'transparent',
                  border:'none', color:accent, outline:'none', textAlign:'center', width:'100%', marginBottom:'.5rem' }} />
            ) : (
              <h2 style={{ fontSize:'clamp(1.4rem,3vw,2.2rem)', fontWeight:'700', color:accent, marginBottom:'.5rem' }}>
                {config?.title || 'Get in Touch'}
              </h2>
            )}
            {editable ? (
              <textarea value={config?.subtitle||''} placeholder="Tagline or description"
                onChange={e => updateField('subtitle',e.target.value)}
                style={{ fontSize:'clamp(.88rem,1.5vw,1rem)', background:'transparent', border:'none',
                  color:text, opacity:.8, textAlign:'center', width:'100%', outline:'none', resize:'none', minHeight:'50px', lineHeight:1.5 }} />
            ) : (
              <p style={{ fontSize:'clamp(.88rem,1.5vw,1rem)', opacity:.8, lineHeight:1.5, maxWidth:'580px', margin:'0 auto', color:text }}>
                {config?.subtitle || 'Connect with us through any of the channels below.'}
              </p>
            )}
          </div>

          {/* ── DESKTOP: 3-col (social | contact | hours) / Mobile: stacked rows ── */}
          <style>{`
            .hpt-contact-cards {
              display: grid;
              grid-template-columns: repeat(auto-fit,minmax(240px,380px));
              justify-content: center;
              gap: clamp(1.25rem,3vw,1.75rem);
              margin-bottom: clamp(2.25rem,4vw,3rem);
            }
            @media (min-width: 769px) {
              /* Flexbox lets however many cards are visible center themselves —
                 3 cards fills the row, 2 cards sits centered, 1 card centers. */
              .hpt-contact-cards {
                display: flex !important;
                flex-wrap: wrap;
                justify-content: center;
                gap: 2rem !important;
                margin-bottom: 2.5rem;
              }
              .hpt-contact-cards > * {
                flex: 0 1 340px;
                min-width: 0;
              }
              .hpt-social-desktop-card { display: flex !important; flex-direction: column; }
              .hpt-social-mobile-row { display: none !important; }
            }
            @media (max-width: 768px) {
              .hpt-social-desktop-card { display: none !important; }
              .hpt-social-mobile-row { display: block !important; }
            }
          `}</style>

          {/* Mobile social row (unchanged behaviour) */}
          {(editable || platforms.some(p => links[p])) && (
            <div className="hpt-social-mobile-row" style={{ textAlign:'center', marginBottom:'clamp(1.5rem,3vw,2rem)' }}>
              <h3 style={{ fontSize:'.78rem', fontWeight:'700', color:text, opacity:.55, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:'1rem' }}>
                Follow Us
              </h3>
              <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'.65rem' }}>
                {platforms.map(p => {
                  const label = p.charAt(0).toUpperCase() + p.slice(1);
                  if (editable) {
                    return (
                      <div key={p} style={{ display:'flex', alignItems:'center', gap:'.5rem',
                        background:`${accent}0d`, border:`1px solid ${accent}25`, borderRadius:'999px',
                        padding:'.35rem .9rem .35rem .35rem' }}>
                        <div style={{ width:'30px', height:'30px', borderRadius:'50%', background:`${accent}18`,
                          display:'flex', alignItems:'center', justifyContent:'center', color:accent, flexShrink:0 }}>
                          <SocialIcon p={p} />
                        </div>
                        <input type="url" value={links[p]||''} placeholder={`${label} URL`}
                          onChange={e => updateLink(p,e.target.value)}
                          style={{ background:'transparent', border:'none', outline:'none', color:text, fontSize:'.82rem', width:'128px' }} />
                      </div>
                    );
                  }
                  if (!links[p]) return null;
                  return (
                    <a key={p} href={links[p]} target="_blank" rel="noopener noreferrer" title={label}
                      style={{ width:'46px', height:'46px', borderRadius:'50%', background:`${accent}15`,
                        display:'flex', alignItems:'center', justifyContent:'center', color:accent, textDecoration:'none',
                        transition:'all .25s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.background=accent; e.currentTarget.style.color='#fff'; e.currentTarget.style.transform='translateY(-3px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background=`${accent}15`; e.currentTarget.style.color=accent; e.currentTarget.style.transform=''; }}>
                      <SocialIcon p={p} />
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* 3-col grid on desktop, 2-col on mobile */}
          <div className="hpt-contact-cards">

            {/* ── Social links — desktop 3rd col ── */}
            {(editable || platforms.some(p => links[p])) && (
              <div className="hpt-social-desktop-card" style={{ display:'none', background:`${accent}0a`, border:`1px solid ${accent}18`, borderRadius:'18px', padding:'1.5rem', textAlign:'center', flexDirection:'column' }}>
                <h3 style={{ fontSize:'.82rem', fontWeight:'700', marginBottom:'1.1rem', color:text, display:'flex', alignItems:'center', justifyContent:'center', gap:'.5rem', textTransform:'uppercase', letterSpacing:'.06em', opacity:.85 }}>
                  <Share size={16} color={accent}/> Follow Us
                </h3>
                <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'.6rem' }}>
                  {platforms.map(p => {
                    const label = p.charAt(0).toUpperCase() + p.slice(1);
                    if (editable) {
                      return (
                        <div key={p} style={{ display:'flex', alignItems:'center', gap:'.4rem', width:'100%',
                          background:`${accent}08`, border:`1px solid ${accent}20`, borderRadius:'8px', padding:'.35rem .65rem' }}>
                          <div style={{ width:'26px', height:'26px', borderRadius:'50%', background:`${accent}18`,
                            display:'flex', alignItems:'center', justifyContent:'center', color:accent, flexShrink:0 }}>
                            <SocialIcon p={p} />
                          </div>
                          <input type="url" value={links[p]||''} placeholder={label}
                            onChange={e => updateLink(p,e.target.value)}
                            style={{ background:'transparent', border:'none', outline:'none', color:text, fontSize:'.8rem', flex:1, minWidth:0 }} />
                        </div>
                      );
                    }
                    if (!links[p]) return null;
                    return (
                      <a key={p} href={links[p]} target="_blank" rel="noopener noreferrer" title={label}
                        style={{ width:'44px', height:'44px', borderRadius:'50%', background:`${accent}15`,
                          display:'flex', alignItems:'center', justifyContent:'center', color:accent, textDecoration:'none',
                          transition:'all .25s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.background=accent; e.currentTarget.style.color='#fff'; e.currentTarget.style.transform='translateY(-3px)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background=`${accent}15`; e.currentTarget.style.color=accent; e.currentTarget.style.transform=''; }}>
                        <SocialIcon p={p} />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contact info — hidden on the public page when nothing was filled in,
                or when a field still holds an old auto-seeded example value the
                owner never actually edited (editor always shows it, prefilled
                with placeholders, so the owner has somewhere to type). */}
            {(editable || realField('email') || realField('phone') || effectiveAddress) && (
              <div style={{ background:`${accent}0a`, border:`1px solid ${accent}18`, borderRadius:'18px', padding:'1.5rem', textAlign:'center' }}>
                <h3 style={{ fontSize:'.82rem', fontWeight:'700', marginBottom:'1.1rem', color:text, display:'flex', alignItems:'center', justifyContent:'center', gap:'.5rem', textTransform:'uppercase', letterSpacing:'.06em', opacity:.85 }}>
                  <Mail size={16} color={accent}/> Contact Info
                </h3>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'.85rem' }}>
                  {[['email','email','info@example.com'],['phone','tel','(555) 123-4567']].map(([field,type,ph]) => (
                    realField(field) || editable ? (
                      <div key={field} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'.85rem' }}>
                        {field==='email' ? <Mail size={16} color={accent}/> : <Phone size={16} color={accent}/>}
                        {editable ? (
                          <input type={type} value={config?.[field]||''} placeholder={ph}
                            onChange={e => updateField(field,e.target.value)}
                            style={{ flex:1, background:'transparent', border:'none', color:text, outline:'none', fontSize:'.9rem', textAlign:'center' }} />
                        ) : (
                          <a href={field === 'email' ? `mailto:${realField(field)}` : `tel:${realField(field)}`}
                            style={{ color:text, fontSize:'.9rem', textDecoration:'none' }}
                            onMouseEnter={e => e.currentTarget.style.color = accent}
                            onMouseLeave={e => e.currentTarget.style.color = text}>
                            {realField(field)}
                          </a>
                        )}
                      </div>
                    ) : null
                  ))}
                  {(effectiveAddress || editable) && (
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'center', gap:'.85rem' }}>
                      <MapPin size={16} color={accent} style={{ flexShrink:0, marginTop:'.1rem' }}/>
                      {editable ? (
                        <div style={{ flex:1 }}>
                          <textarea value={config?.address||''} placeholder="Leave blank to auto-sync from site settings"
                            onChange={e => updateField('address',e.target.value)}
                            style={{ width:'100%', background:'transparent', border:`1px solid ${accent}20`,
                              borderRadius:'4px', color:text, padding:'.4rem', minHeight:'60px', resize:'vertical', fontSize:'.9rem', textAlign:'center', boxSizing:'border-box' }} />
                          {!realField('address') && autoAddress && (
                            <div style={{ fontSize:'.72rem', color:`${accent}90`, marginTop:'.25rem', textAlign:'center' }}>
                              Auto-synced from site settings: {autoAddress.replace(/\n/g, ' · ')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span
                          onClick={() => {
                            const q = encodeURIComponent(effectiveAddress);
                            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                            window.open(isIOS ? `maps://maps.apple.com/?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`, '_blank', 'noopener');
                          }}
                          style={{ color:text, fontSize:'.9rem', lineHeight:1.55, whiteSpace:'pre-line', cursor:'pointer', textDecoration:'underline', textDecorationColor:`${accent}50` }}
                          onMouseEnter={e => e.currentTarget.style.color = accent}
                          onMouseLeave={e => e.currentTarget.style.color = text}>
                          {fmtAddress(effectiveAddress)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hours */}
            <div style={{ background:`${accent}0a`, border:`1px solid ${accent}18`, borderRadius:'18px', padding:'1.5rem' }}>
              <h3 style={{ fontSize:'.82rem', fontWeight:'700', marginBottom:'1.1rem', color:text, display:'flex', alignItems:'center', justifyContent:'center', gap:'.5rem', textTransform:'uppercase', letterSpacing:'.06em', opacity:.85 }}>
                <Clock size={16} color={accent}/> Business Hours
              </h3>
              <div style={{ display:'flex', flexDirection:'column', gap:'.5rem' }}>
                {[['Mon – Fri','9:00 AM – 6:00 PM'],['Saturday','10:00 AM – 4:00 PM'],['Sunday','Closed']].map(([d,h]) => (
                  <div key={d} style={{ display:'flex', justifyContent:'space-between',
                    padding:'.4rem 0', borderBottom:`1px solid ${accent}10` }}>
                    <span style={{ color:text, fontWeight:'500', fontSize:'.86rem' }}>{d}</span>
                    <span style={{ color:text, opacity:.75, fontSize:'.86rem' }}>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ background:`${accent}08`, padding:'1.25rem 2rem', textAlign:'center', borderTop:`1px solid ${accent}15` }}>
          <div style={{ maxWidth:'1100px', margin:'0 auto', display:'flex', justifyContent:'center', alignItems:'center', flexWrap:'wrap', gap:'.75rem 1.5rem' }}>
            <div style={{ color:text, opacity:.75, fontSize:'.85rem' }}>
              © {new Date().getFullYear()} {shopData?.name || shopData?.shopName || 'Your Business'}. All rights reserved.
            </div>
            <div style={{ display:'flex', gap:'1.5rem' }}>
              {['Privacy Policy','Terms of Service'].map(l => (
                <span key={l} style={{ color:text, opacity:.7, fontSize:'.85rem', cursor:'default' }}>{l}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

// ─── MAP SECTION ──────────────────────────────────────────────────────────────
// Fits the map view to all of the shop's saved locations (Shop Settings > Locations)
const MapFitBounds = ({ locations }) => {
  const map = useMap();
  useEffect(() => {
    if (locations.length > 1) {
      const bounds = L.latLngBounds(locations.map(l => [l.coordinates.lat, l.coordinates.lng]));
      map.fitBounds(bounds, { padding: [30, 30] });
    } else if (locations.length === 1) {
      map.setView([locations[0].coordinates.lat, locations[0].coordinates.lng], 14);
    }
  }, [locations, map]);
  return null;
};

export const MapSection = ({ config, theme, editable, onUpdate, shopData }) => {
  const accent  = theme?.colors?.accent   || '#6366f1';
  const text    = theme?.colors?.text     || '#fff';
  const surface = theme?.colors?.surface  || '#1a1a2e';

  const locations = (shopData?.masterAddresses || []).filter(a => a?.coordinates?.lat && a?.coordinates?.lng);
  const primary = locations[0];

  const openInMaps = () => {
    const q = encodeURIComponent(primary?.address || `${primary?.coordinates?.lat},${primary?.coordinates?.lng}`);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    window.open(isIOS ? `maps://maps.apple.com/?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`, '_blank', 'noopener');
  };

  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) {
          .hpt-map-outer { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); }
          .hpt-map-layout { display: grid; grid-template-columns: 280px 1fr; gap: 2.5rem; align-items: start; }
          .hpt-map-info { padding-top: .5rem; }
        }
        @media (max-width: 768px) {
          .hpt-map-outer { padding: clamp(.5rem,1.5vw,1rem) clamp(.75rem,2vw,1.5rem); }
          .hpt-map-layout { display: block; }
          .hpt-map-info { margin-top: .75rem; }
        }
      `}</style>
      <div className="hpt-map-outer">
        <div style={{ maxWidth:'1320px', margin:'0 auto' }}>
        {locations.length === 0 ? (
          <>
            <SectionHead title={config?.title || 'Find Us'} accent={accent} text={text}
              editable={editable} config={config} onUpdate={onUpdate} placeholder="Map section title" />
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
              gap:'.75rem', height:'clamp(180px,25vw,260px)', borderRadius:'16px',
              border:`2px dashed ${accent}30`, background:`${surface}40`, color:text, opacity:.5 }}>
              <MapPin size={36} />
              <span style={{ fontSize:'.85rem', textAlign:'center', padding:'0 1rem' }}>
                {editable ? 'No location set — add one in Shop Settings › Locations' : 'Location coming soon'}
              </span>
            </div>
          </>
        ) : (
          <div className="hpt-map-layout">
            {/* ── Info sidebar (desktop left col) ── */}
            <div className="hpt-map-info">
              <SectionHead title={config?.title || 'Find Us'} accent={accent} text={text} alignLeft
                editable={editable} config={config} onUpdate={onUpdate} placeholder="Map section title" />
              <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {locations.map((loc, i) => (
                  <div key={loc.id || i} style={{ display:'flex', alignItems:'flex-start', gap:'.65rem' }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background:`${accent}18`,
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2 }}>
                      <MapPin size={15} color={accent} />
                    </div>
                    <div>
                      {loc.label && <div style={{ fontSize:'.78rem', fontWeight:'700', color:accent, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'.2rem' }}>{loc.label}</div>}
                      <div style={{ fontSize:'.88rem', color:text, opacity:.85, lineHeight:1.5 }}>{fmtAddress(loc.address)}</div>
                    </div>
                  </div>
                ))}
                <button onClick={openInMaps}
                  style={{ display:'flex', alignItems:'center', gap:'.5rem', background:`${accent}18`,
                    border:`1px solid ${accent}35`, borderRadius:10, padding:'.55rem 1rem',
                    color:accent, fontSize:'.82rem', fontWeight:700, cursor:'pointer', width:'fit-content',
                    transition:'all .2s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.background=accent; e.currentTarget.style.color='#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background=`${accent}18`; e.currentTarget.style.color=accent; }}>
                  <MapPin size={14}/> Open in Maps
                </button>
              </div>
            </div>

            {/* ── Map viewer (desktop right col) ── */}
            <div style={{ position:'relative', borderRadius:'16px', overflow:'hidden',
              border:`1px solid ${accent}30`, boxShadow:`0 8px 36px ${accent}18`,
              height:'clamp(240px,38vw,480px)', cursor:'pointer' }}
              onClick={openInMaps}>
              <MapContainer center={[primary.coordinates.lat, primary.coordinates.lng]} zoom={14}
                style={{ height:'100%', width:'100%' }}
                scrollWheelZoom={false} dragging={false} touchZoom={false}
                doubleClickZoom={false} keyboard={false} zoomControl={false}
                attributionControl={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapFitBounds locations={locations} />
                {locations.map(loc => (
                  <Marker key={loc.id} position={[loc.coordinates.lat, loc.coordinates.lng]}>
                    <Popup>{loc.address}</Popup>
                  </Marker>
                ))}
              </MapContainer>
              <div style={{ position:'absolute', inset:0, zIndex:1000 }} />
              <div style={{ position:'absolute', bottom:10, right:10, zIndex:1001,
                background:`${surface}e8`, border:`1px solid ${accent}40`, borderRadius:20,
                padding:'5px 10px', display:'flex', alignItems:'center', gap:5,
                fontSize:'0.72rem', fontWeight:700, color:accent, backdropFilter:'blur(6px)' }}>
                <MapPin size={12} /> Open in Maps
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </SectionWrapper>
  );
};

// ─── FEATURED ITEMS SECTION ───────────────────────────────────────────────────
// Mirrors the public Shop tab's own Featured card display (FeaturedItem cards +
// the same 1×/2×/3× density toggle) instead of a bespoke grid, so browsing a
// shop's Home tab and its Shop tab feel like the same product.
export const FeaturedItemsSection = ({ config, theme, shopData, shopItems, editable, onUpdate, onItemClick, onOrderClick, onLikeClick, isAuthenticated }) => {
  const featured  = (shopItems || []).filter(i => !i.deleted && i.isFeatured);
  const fallback  = (shopItems || []).filter(i => !i.deleted).slice(0, config?.itemCount || 5);
  const items     = (featured.length > 0 ? featured : fallback).slice(0, config?.itemCount || 5);

  // In public view, only hide when there's truly nothing to show (no items in
  // the shop at all) — the fallback above already matches what the editor
  // preview shows, so an owner who added this section without explicitly
  // marking anything Featured still sees the same items on the live page.
  if (!editable && items.length === 0) return null;

  const accent  = theme?.colors?.accent   || '#6366f1';
  const surface = theme?.colors?.surface  || '#1a1a2e';
  const text    = theme?.colors?.text     || '#fff';

  const cols = Math.min(config?.columns || 2, Math.max(1, items.length));
  const scrollId = `hero-scroll-home-featured-${config?.id || 'items'}`;

  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) { .hpt-fi-outer { padding: clamp(2rem,3.5vw,4rem) clamp(3rem,6vw,6rem); } }
        @media (max-width: 768px) { .hpt-fi-outer { padding: clamp(.5rem,1.5vw,1rem) clamp(.75rem,2vw,1.5rem); } }
      `}</style>
      <div className="hpt-fi-outer">
        <div className="hpt-wrap">
        <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between',
          marginBottom:'1.5rem', gap:'1rem', flexWrap:'wrap' }}>
          <SectionHead title={config?.title || 'Featured'} accent={accent} text={text} alignLeft
            editable={editable} config={config} onUpdate={onUpdate} placeholder="Section title" />
          <div style={{ display:'flex', gap:'.6rem', alignItems:'center', flexShrink:0 }}>
            {items.length > 0 && (
              <span style={{ fontSize:'.68rem', fontWeight:'700', color:accent, opacity:.75, letterSpacing:'.06em', textTransform:'uppercase' }}>
                {items.length} {items.length===1?'item':'items'}
              </span>
            )}
            {items.length > 1 && (
              <span
                onClick={() => onUpdate({ ...config, columns: (config?.columns||2) === 1 ? 2 : (config?.columns||2) === 2 ? 3 : 1 })}
                title={cols === 1 ? 'Switch to 2 columns' : cols === 2 ? 'Switch to 3 columns' : 'Switch to 1 column'}
                style={{ fontSize:'.75rem', fontWeight:800, display:'inline-flex', alignItems:'center', gap:'.15rem',
                  borderRadius:4, border:`1px solid ${accent}40`, padding:'.25rem .45rem', color:accent, cursor:'pointer', userSelect:'none' }}
              >{cols}×<Grid size={12} /></span>
            )}
            {editable && (
              <select value={config?.itemCount||5} onChange={e => onUpdate({ ...config, itemCount:parseInt(e.target.value) })}
                style={{ padding:'.35rem .6rem', borderRadius:'8px', background:surface, color:text,
                  border:`1px solid ${accent}40`, fontSize:'.78rem' }}>
                {[2,3,4,5,6,8].map(n => <option key={n} value={n}>{n} items</option>)}
              </select>
            )}
          </div>
        </div>

        {items.length === 0 ? (
          <div style={{ textAlign:'center', padding:'4rem 2rem', opacity:.5 }}>
            <Package size={52} color={accent} style={{ margin:'0 auto 1rem', opacity:.35, display:'block' }}/>
            <h3 style={{ color:text, margin:'0 0 .4rem', fontSize:'1rem' }}>No featured items yet</h3>
            <p style={{ color:text, opacity:.6, fontSize:'.82rem', margin:0 }}>Mark items as featured in your shop to show them here</p>
          </div>
        ) : cols === 1 ? (
          <div style={{ position:'relative' }}>
            <HeroScrollContainer id={scrollId} theme={theme}>
              {items.map((item, i) => (
                <FeaturedItem
                  key={item.id}
                  style={{ animationDelay:`${i * 0.07}s` }}
                  item={{ ...item, shopName: shopData?.name, isService:false }}
                  theme={theme}
                  onItemClick={onItemClick}
                  onOrderClick={onOrderClick}
                  onLikeClick={onLikeClick}
                  isAuthenticated={isAuthenticated}
                  showTopBar={false}
                  compactBottom={true}
                  columns={2}
                  disableZoom={true}
                />
              ))}
            </HeroScrollContainer>
            {items.length > 1 && (() => {
              const btnBase = { position:'absolute', top:'50%', transform:'translateY(-50%)', background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:'50%', width:'36px', height:'36px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', zIndex:10, color:'#fff', padding:0 };
              return (<>
                <button style={{ ...btnBase, left:'1rem' }} onClick={() => { const el = document.getElementById(scrollId); el?.scrollBy({ left: -el.offsetWidth, behavior:'smooth' }); }}><ChevronLeft size={18} /></button>
                <button style={{ ...btnBase, right:'1rem' }} onClick={() => { const el = document.getElementById(scrollId); el?.scrollBy({ left: el.offsetWidth, behavior:'smooth' }); }}><ChevronRight size={18} /></button>
              </>);
            })()}
          </div>
        ) : (
          <CategoryScrollableGrid
            theme={theme}
            $rows={items.length <= 3 ? 1 : 2}
            $desktopRows={items.length >= 5 ? 2 : 1}
            $mobileCols={cols}
          >
            {items.map(item => (
              <FeaturedItem
                key={item.id}
                item={{ ...item, shopName: shopData?.name, isService:false }}
                theme={theme}
                onItemClick={onItemClick}
                onOrderClick={onOrderClick}
                onLikeClick={onLikeClick}
                isAuthenticated={isAuthenticated}
                showTopBar={false}
                compactBottom={true}
                columns={cols}
                disableZoom={true}
              />
            ))}
          </CategoryScrollableGrid>
        )}
        </div>
      </div>
    </SectionWrapper>
  );
};

// ─── PHOTO GALLERY SECTION ────────────────────────────────────────────────────
export const PhotoGallerySection = ({ config, theme, editable, onUpdate }) => {
  const [slide, setSlide] = useState(0);
  const [dir, setDir] = useState(1); // 1 = forward, -1 = back — flips the crossfade direction
  const [compressing, setCompressing] = useState(false);
  const touchRef = useRef({ x: 0, y: 0, active: false });

  const accent  = theme?.colors?.accent   || '#6366f1';
  const surface = theme?.colors?.surface  || '#1a1a2e';
  const text    = theme?.colors?.text     || '#fff';
  const bg      = theme?.colors?.background || '#0f0f1a';

  const getDisplayImages = () => {
    if (!config?.images) return [];
    return config.images.map(img => {
      if (!img) return null;
      if (typeof img === 'string') return img;
      if (img.preview) return img.preview;
      return null;
    }).filter(Boolean);
  };

  const imgs = getDisplayImages();

  // Compresses BEFORE generating the preview, instead of only at save time —
  // a raw 20-48MP phone photo was getting FileReader.readAsDataURL'd (and its
  // full preview held in state) as soon as it was picked, which is what made
  // this hang on mobile; save-time compression never got a chance to help
  // since the freeze already happened before Save was even clicked.
  const addImage = async (file) => {
    // Unlike compression (soft, falls back below), file type/size is a hard
    // requirement — same 10MB cap as every other upload path in the app.
    try {
      validateImageFile(file, 20);
    } catch (err) {
      alert(err.message || 'Invalid image file.');
      return;
    }
    setCompressing(true);
    // Mirrors bulk upload: compression is a size optimization, not a hard
    // requirement — a failure falls back to the original file instead of
    // blocking the user from adding their photo at all.
    let compressedFile;
    try {
      compressedFile = await compressProductImage(file);
    } catch (err) {
      console.warn('⚠️ Compression failed, using original file uncompressed:', err);
      compressedFile = file;
    }
    try {
      const preview = await readFileAsDataURL(compressedFile);
      const cur = config?.images || [];
      onUpdate({ ...config, images:[...cur, { file: compressedFile, preview, name:compressedFile.name, type:compressedFile.type, size:compressedFile.size, id:`img-${Date.now()}` }] });
      setCompressing(false);
    } catch (err) {
      console.error('Failed to add gallery image:', err);
      alert(err.message || 'Failed to process image. Please try again.');
      setCompressing(false);
    }
  };
  const removeImage = (i) => {
    const next = (config?.images || []).filter((_,idx) => idx!==i);
    onUpdate({ ...config, images:next });
    if (slide >= next.length) setSlide(Math.max(0, next.length-1));
  };
  const goTo = (i) => { setDir(i > slide ? 1 : -1); setSlide(i); };
  const prev = () => { setDir(-1); setSlide(s => (s-1+imgs.length)%imgs.length); };
  const next = () => { setDir(1); setSlide(s => (s+1)%imgs.length); };

  // Swipe-to-navigate — horizontal drags advance the slide; near-vertical
  // drags are left alone so the page can still scroll normally.
  const SWIPE_THRESHOLD = 40;
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, active: true };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current.active || imgs.length < 2) return;
    touchRef.current.active = false;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next(); else prev();
    }
  };

  return (
    <SectionWrapper>
      <style>{`
        @keyframes hpt-gal-in-fwd  { from { opacity:0; transform:scale(1.04) translateX(2%); } to { opacity:1; transform:scale(1) translateX(0); } }
        @keyframes hpt-gal-in-back { from { opacity:0; transform:scale(1.04) translateX(-2%); } to { opacity:1; transform:scale(1) translateX(0); } }
        .hpt-gal-main { touch-action: pan-y; }
        .hpt-gal-main:hover .hpt-gal-img { transform: scale(1.035); }
        .hpt-gal-img { transition: transform .6s cubic-bezier(.4,0,.2,1); will-change: transform; }
        .hpt-gal-arrow { transition: background .2s ease, transform .2s ease, opacity .2s ease; }
        .hpt-gal-arrow:hover { transform: translateY(-50%) scale(1.08); }
        .hpt-gal-arrow:active { transform: translateY(-50%) scale(.94); }
        .hpt-gal-thumb { transition: transform .2s ease, border-color .2s ease, opacity .2s ease, box-shadow .2s ease; }
        .hpt-gal-thumb:hover { transform: translateY(-3px); opacity: 1 !important; }
      `}</style>

      <style>{`
        @media (min-width: 769px) {
          .hpt-gal-outer { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); }
          .hpt-gal-layout { display: grid; grid-template-columns: 1fr 88px; gap: 1rem; align-items: start; max-width: 1320px; margin: 0 auto; }
          .hpt-gal-vthumbs { display: flex !important; flex-direction: column; gap: .5rem; max-height: clamp(300px,50vw,560px); overflow-y: auto; scrollbar-width: none; }
          .hpt-gal-vthumbs::-webkit-scrollbar { display: none; }
          .hpt-gal-hthumbs { display: none !important; }
        }
        @media (max-width: 768px) {
          .hpt-gal-outer { padding: clamp(.5rem,1.5vw,1rem) clamp(.75rem,2vw,1.5rem); }
          .hpt-gal-layout { display: block; max-width: none; }
          .hpt-gal-vthumbs { display: none !important; }
          .hpt-gal-hthumbs { display: flex !important; }
        }
      `}</style>
      <div className="hpt-gal-outer">
      <div className="hpt-gal-layout">
        {/* ── Left col: header + viewer + mobile horizontal thumbs ── */}
        <div>
        <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between',
          marginBottom:'1.25rem', gap:'.75rem', flexWrap:'wrap' }}>
          <SectionHead title={config?.title || 'Gallery'} accent={accent} text={text} alignLeft
            editable={editable} config={config} onUpdate={onUpdate} placeholder="Gallery title" />
          {!editable && imgs.length > 0 && (
            <span style={{ fontSize:'.68rem', fontWeight:'700', color:accent, opacity:.75, letterSpacing:'.06em', textTransform:'uppercase' }}>
              {imgs.length} {imgs.length===1?'photo':'photos'}
            </span>
          )}
        </div>

        {/* Main viewer */}
        <div className="hpt-gal-main"
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
          style={{ position:'relative', borderRadius:'20px', overflow:'hidden',
          background:`${bg}80`, border:`1px solid ${accent}28`,
          boxShadow:`0 22px 52px ${accent}1c, 0 8px 24px rgba(0,0,0,.3)`, marginBottom:'1rem',
          aspectRatio: imgs.length > 0 ? undefined : '16/7',
          height: imgs.length > 0 ? 'clamp(240px,50vw,560px)' : 'clamp(180px,30vw,320px)' }}>
          {imgs.length > 0 ? (
            <>
              <img key={slide} src={imgs[slide]} alt={`Gallery ${slide+1}`} className="hpt-gal-img"
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block',
                  animation:`${dir>0?'hpt-gal-in-fwd':'hpt-gal-in-back'} .45s cubic-bezier(.4,0,.2,1)` }}
                onError={e => { e.target.style.display='none'; }} />
              {/* Gradient overlay at bottom */}
              <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'38%',
                background:'linear-gradient(to top,rgba(0,0,0,.55),transparent)', pointerEvents:'none' }} />
              {/* Slide counter */}
              <div style={{ position:'absolute', bottom:'1rem', left:'50%', transform:'translateX(-50%)',
                background:'rgba(0,0,0,.55)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
                color:'#fff', padding:'.28rem .85rem', borderRadius:'20px', fontSize:'.72rem', fontWeight:'700',
                letterSpacing:'.04em', border:'1px solid rgba(255,255,255,.12)' }}>
                {slide+1} / {imgs.length}
              </div>
              {imgs.length > 1 && (
                <>
                  {[['left',prev,<ChevronLeft size={20}/>],['right',next,<ChevronRight size={20}/>]].map(([side,fn,icon]) => (
                    <button key={side} className="hpt-gal-arrow" onClick={fn} style={{
                      position:'absolute', [side]:'clamp(.5rem,2vw,1rem)', top:'50%', transform:'translateY(-50%)',
                      background:'rgba(0,0,0,.5)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
                      border:`1px solid ${accent}55`,
                      borderRadius:'50%', width:'clamp(34px,8vw,46px)', height:'clamp(34px,8vw,46px)',
                      color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                      boxShadow:'0 4px 14px rgba(0,0,0,.35)', zIndex:2 }}
                      onMouseEnter={e => e.currentTarget.style.background=`${accent}c0`}
                      onMouseLeave={e => e.currentTarget.style.background='rgba(0,0,0,.5)'}>
                      {icon}
                    </button>
                  ))}
                  {/* Dots */}
                  <div style={{ position:'absolute', top:'1rem', right:'1rem', display:'flex', gap:'.35rem', zIndex:2,
                    background:'rgba(0,0,0,.4)', backdropFilter:'blur(8px)', padding:'.4rem .5rem', borderRadius:'20px' }}>
                    {imgs.map((_,i) => (
                      <button key={i} onClick={() => goTo(i)} style={{
                        width: i===slide ? '18px' : '6px', height:'6px',
                        borderRadius:'4px', border:'none',
                        background: i===slide ? accent : 'rgba(255,255,255,.5)',
                        boxShadow: i===slide ? `0 0 8px ${accent}90` : 'none',
                        cursor:'pointer', transition:'all .25s ease', padding:0 }} />
                    ))}
                  </div>
                </>
              )}
              {editable && (
                <button onClick={() => removeImage(slide)} style={{
                  position:'absolute', top:'1rem', left:'1rem', background:'rgba(239,68,68,.88)',
                  border:'none', borderRadius:'50%', width:'32px', height:'32px', color:'#fff',
                  boxShadow:'0 4px 14px rgba(0,0,0,.35)',
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3, fontSize:'1.1rem' }}>
                  ×
                </button>
              )}
            </>
          ) : (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%',
              flexDirection:'column', gap:'.85rem', color:text, opacity:.6 }}>
              <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:`${accent}18`,
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Camera size={32} color={accent} />
              </div>
              <span style={{ fontSize:'.9rem' }}>No images yet</span>
            </div>
          )}
        </div>

        {/* Thumbnails + add button (mobile horizontal; vertical sidebar handles desktop) */}
        {editable && (
          <div className="hpt-gal-hthumbs hpt-chip-scroll" style={{ paddingBottom:0 }}>
            {imgs.map((img,i) => (
              <div key={i} className="hpt-gal-thumb" onClick={() => goTo(i)} style={{
                minWidth:'76px', height:'76px', borderRadius:'12px', overflow:'hidden', cursor:'pointer', flexShrink:0,
                scrollSnapAlign:'start',
                border: i===slide ? `2.5px solid ${accent}` : `1.5px solid ${accent}22`,
                boxShadow: i===slide ? `0 6px 18px ${accent}45` : 'none',
                opacity: i===slide ? 1 : .6 }}>
                <img src={img} alt={`thumb-${i}`} style={{ width:'100%', height:'100%', objectFit:'cover' }}
                  onError={e => { e.target.style.display='none'; }} />
              </div>
            ))}
            {imgs.length < 8 && (
              <label style={{ minWidth:'76px', height:'76px', borderRadius:'12px', cursor: compressing ? 'default' : 'pointer', flexShrink:0,
                scrollSnapAlign:'start',
                border:`2px dashed ${accent}45`, background:`${surface}40`,
                display:'flex', alignItems:'center', justifyContent:'center', color:accent,
                fontSize: compressing ? '.62rem' : '1.5rem', fontWeight: compressing ? 700 : 400,
                textAlign:'center', padding: compressing ? '.4rem' : 0,
                transition:'background .2s ease', opacity: compressing ? .6 : 1 }}>
                {compressing ? 'Processing…' : '+'}
                <input type="file" accept="image/*" disabled={compressing} style={{ display:'none' }}
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; f && addImage(f); }} />
              </label>
            )}
          </div>
        )}

        {/* Mobile horizontal thumbnail strip */}
        {!editable && imgs.length > 1 && (
          <div className="hpt-gal-hthumbs hpt-chip-scroll" style={{ paddingBottom:0 }}>
            {imgs.map((img,i) => (
              <div key={i} className="hpt-gal-thumb" onClick={() => goTo(i)} style={{
                minWidth:'64px', height:'64px', borderRadius:'10px', overflow:'hidden', cursor:'pointer', flexShrink:0,
                scrollSnapAlign:'start',
                border: i===slide ? `2px solid ${accent}` : `1px solid ${accent}20`,
                boxShadow: i===slide ? `0 6px 16px ${accent}40` : 'none',
                opacity: i===slide ? 1 : .55 }}>
                <img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                  onError={e => { e.target.style.display='none'; }} />
              </div>
            ))}
          </div>
        )}
        </div>{/* /left col */}

        {/* ── Desktop vertical thumbnail sidebar ── */}
        {imgs.length > 1 && (
          <div className="hpt-gal-vthumbs" style={{ display:'none' }}>
            {imgs.map((img,i) => (
              <div key={i} className="hpt-gal-thumb" onClick={() => goTo(i)} style={{
                width:'88px', height:'66px', borderRadius:'10px', overflow:'hidden', cursor:'pointer', flexShrink:0,
                border: i===slide ? `2px solid ${accent}` : `1.5px solid ${accent}22`,
                boxShadow: i===slide ? `0 4px 14px ${accent}50` : 'none',
                opacity: i===slide ? 1 : .5 }}>
                <img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}
                  onError={e => { e.target.style.display='none'; }} />
              </div>
            ))}
            {editable && imgs.length < 8 && (
              <label style={{ width:'88px', height:'66px', borderRadius:'10px', cursor: compressing ? 'default' : 'pointer', flexShrink:0,
                border:`2px dashed ${accent}45`, background:`${surface}40`,
                display:'flex', alignItems:'center', justifyContent:'center', color:accent,
                fontSize: compressing ? '.6rem' : '1.4rem', textAlign:'center', padding: compressing ? '.3rem' : 0 }}>
                {compressing ? '…' : '+'}
                <input type="file" accept="image/*" disabled={compressing} style={{ display:'none' }}
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; f && addImage(f); }} />
              </label>
            )}
          </div>
        )}
      </div>{/* /hpt-gal-layout */}
      </div>{/* /hpt-gal-outer */}
    </SectionWrapper>
  );
};

// ─── MISSION STATEMENT SECTION ────────────────────────────────────────────────
export const MissionStatementSection = ({ config, theme, editable, onUpdate, shopData }) => {
  const accent = theme?.colors?.accent || '#6366f1';
  const text   = theme?.colors?.text   || '#fff';
  const bg     = theme?.colors?.background || '#0f0f1a';
  const surface= theme?.colors?.surface || '#1a1a2e';
  const [compressing, setCompressing] = useState(false);

  // Default to shop pfp, then fallback to a solid colour placeholder
  const shopPfp = shopData?.shopProfilePicture
    ? (typeof shopData.shopProfilePicture === 'string'
        ? shopData.shopProfilePicture
        : shopData.shopProfilePicture?.preview || shopData.shopProfilePicture?.url)
    : null;

  const missionImage = config?.image
    ? (typeof config.image === 'string' ? config.image : config.image.preview)
    : shopPfp;

  // Compresses BEFORE generating the preview, instead of only at save time —
  // a raw 20-48MP phone photo was getting FileReader.readAsDataURL'd (and its
  // full preview held in state) as soon as it was picked, which is what made
  // this hang on mobile; save-time compression never got a chance to help
  // since the freeze already happened before Save was even clicked.
  const handleImageUpload = async (file) => {
    // Unlike compression (soft, falls back below), file type/size is a hard
    // requirement — same 10MB cap as every other upload path in the app.
    try {
      validateImageFile(file, 20);
    } catch (err) {
      alert(err.message || 'Invalid image file.');
      return;
    }
    setCompressing(true);
    // Mirrors bulk upload: compression is a size optimization, not a hard
    // requirement — a failure falls back to the original file instead of
    // blocking the user from setting their photo at all.
    let compressedFile;
    try {
      compressedFile = await compressProductImage(file);
    } catch (err) {
      console.warn('⚠️ Compression failed, using original file uncompressed:', err);
      compressedFile = file;
    }
    try {
      const preview = await readFileAsDataURL(compressedFile);
      onUpdate({ ...config, image:{ file: compressedFile, preview, type:compressedFile.type, name:compressedFile.name } });
      setCompressing(false);
    } catch (err) {
      console.error('Failed to set mission image:', err);
      alert(err.message || 'Failed to process image. Please try again.');
      setCompressing(false);
    }
  };

  return (
    <SectionWrapper>
      <style>{`
        .hpt-mission-grid {
          display: grid;
          grid-template-columns: clamp(180px,36%,380px) 1fr;
          gap: clamp(1.5rem,4vw,3.5rem);
          align-items: center;
        }
        @media (max-width: 768px) {
          .hpt-mission-grid { grid-template-columns: 1fr; gap: 1.25rem; }
        }
        @media (min-width: 769px) {
          .hpt-mission-outer { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); }
        }
        @media (max-width: 768px) {
          .hpt-mission-outer { padding: clamp(1.25rem,3vw,2.5rem) clamp(.75rem,2vw,1.5rem); }
        }
      `}</style>
      <div className="hpt-mission-outer" style={{ background:`${surface}60`, borderRadius:'16px',
        maxWidth:'1320px', width:'calc(100% - clamp(1rem,4vw,2rem))', margin:'0 auto' }}>
        <div className="hpt-mission-grid">

          {/* Image */}
          <div style={{ position:'relative', borderRadius:'14px', overflow:'hidden',
            border:`2px solid ${accent}35`, boxShadow:`0 12px 40px ${accent}25`,
            aspectRatio:'1/1', background:`${accent}15`,
            display:'flex', alignItems:'center', justifyContent:'center' }}>
            {missionImage ? (
              <img src={missionImage} alt="Mission"
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            ) : (
              /* No pfp and no custom image — show accent initial */
              <span style={{ fontSize:'4rem', fontWeight:'800', color:accent, opacity:.6 }}>
                {(shopData?.name || '?')[0].toUpperCase()}
              </span>
            )}
            {editable && (
              <div style={{ position:'absolute', inset:0, background: compressing ? 'rgba(0,0,0,.6)' : 'rgba(0,0,0,0)',
                display:'flex', alignItems:'center', justifyContent:'center',
                transition:'background .25s ease', cursor: compressing ? 'default' : 'pointer' }}
                onMouseEnter={e => { if (!compressing) e.currentTarget.style.background='rgba(0,0,0,.5)'; }}
                onMouseLeave={e => { if (!compressing) e.currentTarget.style.background='rgba(0,0,0,0)'; }}
                onClick={() => !compressing && document.getElementById(`mission-img-${config?.id}`)?.click()}>
                <label htmlFor={`mission-img-${config?.id}`} style={{
                  background:accent, color:'#fff', padding:'.65rem 1.25rem', borderRadius:'8px',
                  display:'flex', alignItems:'center', gap:'.5rem', fontWeight:'600', cursor: compressing ? 'default' : 'pointer',
                  opacity: compressing ? 1 : 0, transition:'opacity .25s ease' }}
                  onMouseEnter={e => { if (!compressing) e.currentTarget.style.opacity='1'; }}
                  onMouseLeave={e => { if (!compressing) e.currentTarget.style.opacity='0'; }}>
                  <Camera size={18}/> {compressing ? 'Processing…' : 'Change Image'}
                </label>
                <input id={`mission-img-${config?.id}`} type="file" accept="image/*" disabled={compressing}
                  style={{ display:'none' }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; f && handleImageUpload(f); }} />
              </div>
            )}
          </div>

          {/* Text */}
          <div style={{ display:'flex', flexDirection:'column', gap:'.85rem', alignItems:'center', textAlign:'center' }}>
            <div style={{ width:'2.5rem', height:'3px', background:`linear-gradient(90deg,${accent},${accent}40)`,
              borderRadius:'2px' }} />
            {editable ? (
              <input type="text" value={config?.title||''} placeholder="Mission title"
                onChange={e => onUpdate({ ...config, title:e.target.value })}
                style={{ fontSize:'clamp(1.3rem,3vw,2rem)', fontWeight:'800', fontFamily: theme?.fonts?.heading || 'inherit',
                  background:'transparent', border:'none', borderBottom:`1px solid ${accent}40`,
                  color:accent, outline:'none', width:'100%', letterSpacing:'-.025em', paddingBottom:'.25rem', textAlign:'center' }} />
            ) : (
              <h2 style={{ fontSize:'clamp(1.3rem,3vw,2rem)', fontWeight:'800', color:accent,
                fontFamily: theme?.fonts?.heading || 'inherit',
                margin:0, letterSpacing:'-.025em', lineHeight:1.15 }}>
                {config?.title || 'Our Mission'}
              </h2>
            )}
            {editable ? (
              <textarea value={config?.content||''} placeholder="Mission statement..."
                onChange={e => onUpdate({ ...config, content:e.target.value })}
                style={{ fontSize:'clamp(.9rem,1.8vw,1.05rem)', lineHeight:1.7, background:`${bg}80`,
                  fontFamily: theme?.fonts?.body || 'inherit', textAlign:'center',
                  border:`1px solid ${accent}35`, borderRadius:'10px', color:text,
                  outline:'none', padding:'.85rem', width:'100%', minHeight:'140px', resize:'vertical' }} />
            ) : (
              <p style={{ fontSize:'clamp(.9rem,1.8vw,1.05rem)', lineHeight:1.8, color:text,
                fontFamily: theme?.fonts?.body || 'inherit', textAlign:'center',
                margin:'0 auto', width:'100%', maxWidth:'640px' }}>
                {config?.content || 'We deliver exceptional quality and service, committed to making every experience outstanding.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

// ─── FEATURED VIDEO SECTION ───────────────────────────────────────────────────
export const FeaturedVideoSection = ({ config, theme, editable, onUpdate }) => {
  const accent = theme?.colors?.accent   || '#6366f1';
  const surface= theme?.colors?.surface  || '#1a1a2e';
  const text   = theme?.colors?.text     || '#fff';
  const bg     = theme?.colors?.background || '#0f0f1a';

  const getYouTubeId = (url) => {
    if (!url) return null;
    const r = url.match(/(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return r ? r[1] : null;
  };
  const videoId = config?.videoId || getYouTubeId(config?.youtubeUrl || '');

  // Lazy-load facade: show our own thumbnail + play button first, and only
  // mount the real YouTube player once clicked. Keeps the resting state
  // looking clean and on-brand instead of YouTube's own cluttered poster
  // (channel overlay, suggested-video card, "Watch on YouTube" fallback).
  const [playing, setPlaying] = useState(false);
  const [thumbSrc, setThumbSrc] = useState(null);

  // Once playing, we drive a controls:0 YT.Player through the IFrame API and
  // render our own themed control bar instead of YouTube's native chrome.
  const playerElId = useRef(`hpt-yt-${Math.random().toString(36).slice(2)}`);
  const playerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const hideControlsTimerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const wrapperRef = useRef(null);
  const [playerState, setPlayerState] = useState({ isPlaying:false, current:0, duration:0, muted:false, ready:false });
  const [controlsVisible, setControlsVisible] = useState(true);

  useEffect(() => {
    setPlaying(false);
    setThumbSrc(videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null);
    setPlayerState({ isPlaying:false, current:0, duration:0, muted:false, ready:false });
    setControlsVisible(true);
  }, [videoId]);

  useEffect(() => {
    if (!playing || !videoId) return undefined;
    let cancelled = false;
    loadYouTubeIframeAPI().then((YT) => {
      if (cancelled || !YT) return;
      playerRef.current = new YT.Player(playerElId.current, {
        videoId,
        playerVars: { autoplay:1, controls:0, rel:0, modestbranding:1, iv_load_policy:3, playsinline:1, disablekb:1 },
        events: {
          onReady: (e) => {
            setPlayerState(s => ({ ...s, ready:true, duration: e.target.getDuration() || 0, muted: e.target.isMuted() }));
          },
          onStateChange: (e) => {
            const nowPlaying = e.data === window.YT.PlayerState.PLAYING;
            isPlayingRef.current = nowPlaying;
            setPlayerState(s => ({ ...s, isPlaying: nowPlaying }));
            clearInterval(progressTimerRef.current);
            if (nowPlaying) {
              progressTimerRef.current = setInterval(() => {
                if (playerRef.current?.getCurrentTime) {
                  setPlayerState(s => ({
                    ...s,
                    current: playerRef.current.getCurrentTime() || 0,
                    duration: playerRef.current.getDuration() || s.duration,
                  }));
                }
              }, 400);
              clearTimeout(hideControlsTimerRef.current);
              hideControlsTimerRef.current = setTimeout(() => setControlsVisible(false), 2500);
            } else {
              clearTimeout(hideControlsTimerRef.current);
              setControlsVisible(true);
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      clearInterval(progressTimerRef.current);
      clearTimeout(hideControlsTimerRef.current);
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (err) { /* already gone */ }
        playerRef.current = null;
      }
    };
  }, [playing, videoId]);

  const showControlsTemporarily = () => {
    setControlsVisible(true);
    clearTimeout(hideControlsTimerRef.current);
    if (isPlayingRef.current) {
      hideControlsTimerRef.current = setTimeout(() => setControlsVisible(false), 2500);
    }
  };

  const togglePlay = () => {
    if (!playerRef.current) return;
    if (playerState.isPlaying) playerRef.current.pauseVideo();
    else playerRef.current.playVideo();
    showControlsTemporarily();
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    if (playerRef.current.isMuted()) { playerRef.current.unMute(); setPlayerState(s => ({ ...s, muted:false })); }
    else { playerRef.current.mute(); setPlayerState(s => ({ ...s, muted:true })); }
    showControlsTemporarily();
  };

  const toggleFullscreen = () => {
    if (!wrapperRef.current) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else wrapperRef.current.requestFullscreen?.();
  };

  const seekTo = (e) => {
    if (!playerRef.current || !playerState.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    playerRef.current.seekTo(ratio * playerState.duration, true);
    showControlsTemporarily();
  };

  const fmtTime = (secs) => {
    if (!isFinite(secs) || secs < 0) secs = 0;
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const controlBtnStyle = {
    width:'32px', height:'32px', borderRadius:'50%', flexShrink:0,
    background:`${accent}cc`, border:'none', cursor:'pointer',
    display:'flex', alignItems:'center', justifyContent:'center',
  };

  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) { .hpt-vid-outer { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); } }
        @media (max-width: 768px) { .hpt-vid-outer { padding: clamp(.5rem,1.5vw,1rem) clamp(.75rem,2vw,1.5rem); } }
      `}</style>
      <div className="hpt-vid-outer">
        <div style={{ maxWidth:'1100px', margin:'0 auto' }}>
        <SectionHead title={config?.title || 'Featured Video'} accent={accent} text={text} alignLeft
          editable={editable} config={config} onUpdate={onUpdate} placeholder="Video section title" />

        {editable && (
          <input type="text" value={config?.youtubeUrl||''} placeholder="Paste YouTube URL here"
            onChange={e => onUpdate({ ...config, youtubeUrl:e.target.value, videoId:getYouTubeId(e.target.value) })}
            style={{ width:'100%', padding:'.75rem', marginBottom:'1rem', borderRadius:'10px',
              background:surface, border:`1px solid ${accent}40`, color:text, fontSize:'.9rem' }} />
        )}

        <div style={{ maxWidth:'900px', margin:'0 auto', borderRadius:'20px', overflow:'hidden',
          background:`${bg}80`, border:`1px solid ${accent}35`,
          boxShadow:`0 16px 56px ${accent}22` }}>
          {videoId ? (
            <div style={{ position:'relative', paddingBottom:'56.25%', height:0, background:'#000' }}>
              {playing ? (
                <div
                  ref={wrapperRef}
                  onMouseMove={showControlsTemporarily}
                  onMouseLeave={() => { if (isPlayingRef.current) setControlsVisible(false); }}
                  onClick={togglePlay}
                  style={{ position:'absolute', inset:0, background:'#000', cursor:'pointer' }}
                >
                  <div id={playerElId.current} style={{ width:'100%', height:'100%' }} />

                  {!playerState.ready && (
                    <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center',
                      justifyContent:'center', background:'#000' }}>
                      <div style={{ width:'40px', height:'40px', borderRadius:'50%',
                        border:`3px solid ${accent}40`, borderTopColor:accent,
                        animation:'hpt-spin 0.8s linear infinite' }} />
                    </div>
                  )}

                  {/* Top scrim — YouTube overlays its own title/channel card on hover with no
                      way to resize or remove it (cross-origin, no embed param controls this
                      anymore). Dimming it under our own gradient keeps it from dominating the
                      frame, and ties its visibility to the same fade timing as our controls. */}
                  <div style={{
                    position:'absolute', top:0, left:0, right:0,
                    height:'clamp(48px,14%,84px)',
                    background:'linear-gradient(to bottom, rgba(0,0,0,.55), transparent)',
                    opacity: controlsVisible ? 1 : 0,
                    transition:'opacity .25s ease',
                    pointerEvents:'none',
                  }} />

                  {/* Themed control bar — replaces YouTube's native chrome */}
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      position:'absolute', left:0, right:0, bottom:0,
                      padding:'1.75rem .65rem .55rem',
                      background:'linear-gradient(to top, rgba(0,0,0,.8), transparent)',
                      opacity: controlsVisible ? 1 : 0,
                      pointerEvents: controlsVisible ? 'auto' : 'none',
                      transition:'opacity .25s ease',
                    }}
                  >
                    <div
                      onClick={seekTo}
                      style={{ height:'5px', borderRadius:'4px', background:'rgba(255,255,255,.28)',
                        cursor:'pointer', marginBottom:'.5rem', position:'relative' }}
                    >
                      <div style={{
                        position:'absolute', top:0, left:0, bottom:0, borderRadius:'4px',
                        width:`${playerState.duration ? Math.min(100, (playerState.current / playerState.duration) * 100) : 0}%`,
                        background: accent,
                      }} />
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'.6rem' }}>
                      <button onClick={togglePlay} style={controlBtnStyle} aria-label={playerState.isPlaying ? 'Pause' : 'Play'}>
                        {playerState.isPlaying
                          ? <Pause size={14} color="#fff" fill="#fff" />
                          : <Play size={14} color="#fff" fill="#fff" style={{ marginLeft:'2px' }} />}
                      </button>
                      <button onClick={toggleMute} style={controlBtnStyle} aria-label={playerState.muted ? 'Unmute' : 'Mute'}>
                        {playerState.muted ? <VolumeX size={15} color="#fff" /> : <Volume2 size={15} color="#fff" />}
                      </button>
                      <span style={{ color:'#fff', fontSize:'.72rem', fontVariantNumeric:'tabular-nums', opacity:.85 }}>
                        {fmtTime(playerState.current)} / {fmtTime(playerState.duration)}
                      </span>
                      <div style={{ flex:1 }} />
                      <button onClick={toggleFullscreen} style={controlBtnStyle} aria-label="Fullscreen">
                        <Maximize size={14} color="#fff" />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setPlaying(true)}
                  aria-label="Play video"
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%', padding:0,
                    border:'none', background:'none', cursor:'pointer', display:'block' }}
                >
                  {thumbSrc && (
                    <img src={thumbSrc} alt="" loading="lazy"
                      onError={e => {
                        // maxresdefault.jpg doesn't exist for every video — fall back once
                        if (!e.currentTarget.dataset.fallback) {
                          e.currentTarget.dataset.fallback = '1';
                          e.currentTarget.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                        }
                      }}
                      style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover' }} />
                  )}
                  <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,.25)' }} />
                  <div style={{
                    position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
                    width:'clamp(52px,9vw,72px)', height:'clamp(52px,9vw,72px)', borderRadius:'50%',
                    background: accent, display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow:`0 8px 28px rgba(0,0,0,.4)`,
                  }}>
                    <Play size={28} color="#fff" fill="#fff" style={{ marginLeft:'3px' }} />
                  </div>
                </button>
              )}
            </div>
          ) : (
            <div style={{ height:'clamp(200px,35vw,380px)', display:'flex', alignItems:'center',
              justifyContent:'center', flexDirection:'column', gap:'.85rem', color:text, opacity:.45 }}>
              <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:`${accent}18`,
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Play size={32} color={accent}/>
              </div>
              <span style={{ fontSize:'.9rem' }}>Add a YouTube URL to display video</span>
            </div>
          )}
        </div>
        </div>{/* /maxWidth wrap */}
      </div>{/* /hpt-vid-outer */}
    </SectionWrapper>
  );
};

// ─── HERO BANNER SECTION ──────────────────────────────────────────────────────
export const HeroBannerSection = ({ config, theme, editable, onUpdate, onCtaClick, shopData }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode,   setDragMode]   = useState(false);
  const [focalPoint, setFocalPoint] = useState({ x:50, y:50 });
  const [isLoaded,    setIsLoaded]    = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setIsLoaded(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (config?.focalPoint) setFocalPoint(config.focalPoint);
    else setFocalPoint({ x:50, y:50 });
  }, [config?.id]);

  const handleMouseMove = (e) => {
    if (!isDragging || !dragMode) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setFocalPoint({
        x: Math.max(0, Math.min(100, ((e.clientX-rect.left)/rect.width)*100)),
        y: Math.max(0, Math.min(100, ((e.clientY-rect.top)/rect.height)*100)),
      });
    }
  };
  const handleMouseUp = () => { if (isDragging) setIsDragging(false); };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }
  }, [isDragging, dragMode]);

  const toggleDrag = () => {
    if (dragMode) { onUpdate({ ...config, focalPoint }); setDragMode(false); }
    else setDragMode(true);
  };

  // showContent: true/undefined = full box (headline+subtitle+button),
  // 'button' = keep just the shop/book button, false = hide the whole box.
  const contentMode = config?.showContent === false ? 'hidden' : config?.showContent === 'button' ? 'button' : 'full';
  const showContent = contentMode !== 'hidden';
  const showButtonOnly = contentMode === 'button';
  const contentPosition = config?.contentPosition || 'center';
  const CONTENT_POSITIONS = ['top', 'center', 'bottom'];
  const CONTENT_MODES = ['full', 'button', 'hidden'];
  const cycleContentVisible = () => {
    const idx = CONTENT_MODES.indexOf(contentMode);
    const next = CONTENT_MODES[(idx + 1) % CONTENT_MODES.length];
    onUpdate({ ...config, showContent: next === 'full' ? true : next === 'button' ? 'button' : false });
  };
  const cycleContentPosition = () => {
    const idx = CONTENT_POSITIONS.indexOf(contentPosition);
    onUpdate({ ...config, contentPosition: CONTENT_POSITIONS[(idx + 1) % CONTENT_POSITIONS.length] });
  };
  const toggleZoom = () => onUpdate({ ...config, disableZoom: !config?.disableZoom });

  const handleFileUpload = async (file) => {
    if (file.type.startsWith('video/')) {
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = async () => {
        window.URL.revokeObjectURL(vid.src);
        if (vid.duration > 15) { alert('Video must be 15 seconds or less'); return; }
        if (file.size > 50*1024*1024) { alert('Video must be under 50MB'); return; }
        try {
          const preview = await readFileAsDataURL(file);
          onUpdate({ backgroundImage:preview, backgroundType:'video', focalPoint:{x:50,y:50} });
        } catch (err) {
          console.error('Failed to read video file:', err);
          alert(err.message || 'Failed to process video. Please try again.');
        }
      };
      vid.src = URL.createObjectURL(file);
    } else {
      // Same 10MB cap as every other image upload path, plus compress before
      // generating the preview (was doing FileReader.readAsDataURL directly
      // on the raw file with no cap and no compression at all — the same class
      // of mobile-hanging bug fixed on Mission/Gallery, just never applied here).
      try {
        validateImageFile(file, 20);
      } catch (err) {
        alert(err.message || 'Invalid image file.');
        return;
      }
      let compressedFile;
      try {
        compressedFile = await compressProductImage(file);
      } catch (err) {
        console.warn('⚠️ Compression failed, using original file uncompressed:', err);
        compressedFile = file;
      }
      try {
        const preview = await readFileAsDataURL(compressedFile);
        onUpdate({ backgroundImage:preview, backgroundType:'image', focalPoint:{x:50,y:50} });
      } catch (err) {
        console.error('Failed to read image file:', err);
        alert(err.message || 'Failed to process image. Please try again.');
      }
    }
  };

  const accent      = theme?.colors?.accent || '#6366f1';
  const text        = theme?.colors?.text   || '#fff';
  const headingFont = theme?.fonts?.heading || 'inherit';

  const bgStyle = (() => {
    if (!config?.backgroundImage) return { background:`linear-gradient(135deg,${accent}18,${accent}06)` };
    return { cursor: dragMode ? 'move' : 'default' };
  })();

  return (
    <div style={{ margin:'0', marginBottom:'clamp(1rem,2vw,2rem)', opacity:isLoaded?1:0, transition:'opacity 1.2s ease' }}>
      <SectionWrapper noPadding>
        <style>{`
          @keyframes hpt-hero-zoom { 0%,100%{transform:scale(1)} 50%{transform:scale(1.09)} }
          .hpt-hero-bg { animation: hpt-hero-zoom 22s ease-in-out infinite; }
          .hpt-hero-container { min-height: 88vh; }
          @media (min-width: 769px) {
            .hpt-hero-container {
              min-height: 100vh;
              padding-top: max(calc(clamp(50px, 8vh, 80px) + 1.25rem), 11vh) !important;
            }
          }
          @media (max-width: 768px) {
            .hpt-hero-headline { font-size: clamp(1.3rem, 3.64vw, 2.6rem) !important; }
            .hpt-hero-subtitle { font-size: clamp(0.975rem, 1.82vw, 1.3rem) !important; }
            .hpt-hero-cta { font-size: clamp(0.975rem, 1.69vw, 1.17rem) !important; }
          }
        `}</style>

        <div ref={containerRef} className="hpt-hero-container"
          onMouseDown={() => { if (dragMode && config?.backgroundImage) setIsDragging(true); }}
          style={{ ...bgStyle,
            display:'flex',
            alignItems: contentPosition === 'top' ? 'flex-start' : contentPosition === 'bottom' ? 'flex-end' : 'center',
            justifyContent:'center',
            position:'relative', overflow:'hidden',
            padding:'clamp(1.5rem,4vw,3.5rem) clamp(1rem,3vw,2.5rem)' }}>

          {/* Animated image bg — uses <img> for hardware-decoded crisp rendering */}
          {config?.backgroundType !== 'video' && config?.backgroundImage && (
            <div className="hpt-hero-bg" style={{
              position:'absolute', top:'-8%', left:'-8%', width:'116%', height:'116%',
              zIndex:0, pointerEvents:'none', willChange:'transform',
              animation: config?.disableZoom ? 'none' : undefined }}>
              <img
                src={config.backgroundImage}
                alt=""
                style={{
                  position:'absolute', inset:0, width:'100%', height:'100%',
                  objectFit:'cover', objectPosition:`${focalPoint.x}% ${focalPoint.y}%`,
                  display:'block',
                }}
              />
            </div>
          )}

          {/* Video bg */}
          {config?.backgroundType === 'video' && config?.backgroundImage && (
            <div style={{ position:'absolute', inset:0, overflow:'hidden', zIndex:0 }}>
              <video autoPlay loop muted playsInline
                style={{ position:'absolute', top:'50%', left:'50%', minWidth:'110%', minHeight:'110%',
                  width:'auto', height:'auto', transform:`translate(calc(-50% + ${(focalPoint.x-50)*1.5}%),calc(-50% + ${(focalPoint.y-50)*1.5}%))`,
                  objectFit:'cover', pointerEvents:'none' }}
                src={config.backgroundImage} />
            </div>
          )}

          {/* No-image gradient bg */}
          {!config?.backgroundImage && (
            <div style={{ position:'absolute', inset:0, zIndex:0,
              background:`radial-gradient(ellipse at 30% 50%,${accent}22 0%,transparent 55%), radial-gradient(ellipse at 70% 50%,${accent}14 0%,transparent 55%)`,
              pointerEvents:'none' }} />
          )}

          {/* Dim overlay — improves text contrast over bright background images */}
          {config?.backgroundImage && (
            <div style={{ position:'absolute', inset:0, zIndex:1,
              background:'rgba(0,0,0,0.28)', pointerEvents:'none' }} />
          )}

          {/* Drag crosshair */}
          {editable && dragMode && (
            <>
              <div style={{ position:'absolute', left:`${focalPoint.x}%`, top:0, bottom:0, width:'2px', background:accent, opacity:.6, pointerEvents:'none', zIndex:15 }}/>
              <div style={{ position:'absolute', top:`${focalPoint.y}%`, left:0, right:0, height:'2px', background:accent, opacity:.6, pointerEvents:'none', zIndex:15 }}/>
              <div style={{ position:'absolute', left:`${focalPoint.x}%`, top:`${focalPoint.y}%`, width:'20px', height:'20px',
                border:`3px solid ${accent}`, borderRadius:'50%', transform:'translate(-50%,-50%)',
                pointerEvents:'none', zIndex:16, boxShadow:`0 0 0 2px rgba(0,0,0,.5),0 0 20px ${accent}80` }}/>
              <div style={{ position:'absolute', top:'1rem', right:'1rem', background:accent, color:'#fff',
                padding:'.5rem 1rem', borderRadius:'8px', fontSize:'.82rem', fontWeight:'600', zIndex:20 }}>
                🖱️ Drag to adjust · ✓ to save
              </div>
            </>
          )}

          {/* Content — hidden entirely when contentMode is 'hidden', leaving just the bg.
              When contentMode is 'button', only the shop/book button renders (no headline/
              subtitle box) instead of removing the whole thing. */}
          {showContent && (
          <div style={{ textAlign:'center', position:'relative', zIndex:2, maxWidth:'clamp(min(88vw, 360px), 32vw, 520px)', width:'100%',
            cursor:'auto', pointerEvents: dragMode?'none':'auto' }}
            onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            {showButtonOnly ? (
              editable ? (
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:'.75rem' }}
                  onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                  <span style={{ fontSize:'.8rem', color:text, opacity:.65 }}>Button label:</span>
                  <button
                    onClick={() => onUpdate({ ...config, ctaLabel: (config?.ctaLabel||'shop') === 'shop' ? 'book' : 'shop' })}
                    style={{ background:accent, border:'none', color:'#fff', padding:'.4rem 1.4rem',
                      borderRadius:'20px', fontWeight:'700', fontSize:'.88rem', cursor:'pointer',
                      textTransform:'capitalize', letterSpacing:'.03em', boxShadow:`0 3px 12px ${accent}50` }}>
                    {(config?.ctaLabel||'shop') === 'shop' ? 'Shop' : 'Book'}
                  </button>
                  <span style={{ fontSize:'.72rem', color:text, opacity:.45 }}>click to toggle</span>
                </div>
              ) : (
                <button
                  onClick={onCtaClick}
                  style={{ background:accent, border:'none', color:'#fff',
                    padding:'.65rem 2.5rem', borderRadius:'25px',
                    fontWeight:'700', fontSize:'clamp(.9rem,2vw,1rem)',
                    cursor:'pointer', textTransform:'capitalize', letterSpacing:'.04em',
                    boxShadow:`0 4px 18px ${accent}55`, transition:'all .2s ease' }}
                  onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 8px 28px ${accent}70`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=`0 4px 18px ${accent}55`; }}
                >
                  {(config?.ctaLabel||'shop') === 'shop' ? 'Shop' : 'Book'}
                </button>
              )
            ) : editable ? (
              <>
                <input type="text" value={config?.headline||''} placeholder="Enter headline"
                  onChange={e => onUpdate({ ...config, headline:e.target.value })}
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize:'clamp(1.5rem,6vw,4rem)', fontWeight:'900', margin:'0 0 .5rem',
                    fontFamily:headingFont,
                    background:'transparent', border:'none', color:accent, textAlign:'center', width:'100%',
                    outline:'none', lineHeight:1.1, cursor:'text' }} />
                <div style={{ height:'2px', background:`linear-gradient(90deg,transparent,${accent}80,transparent)`, marginBottom:'.75rem' }}/>
                <textarea value={config?.subtitle||''} placeholder="Enter subtitle"
                  onChange={e => onUpdate({ ...config, subtitle:e.target.value })}
                  onClick={e => e.stopPropagation()}
                  style={{ fontSize:'clamp(.9rem,2.5vw,1.4rem)', opacity:.9, background:'transparent',
                    border:'none', color:text, textAlign:'center', width:'100%', outline:'none',
                    resize:'none', minHeight:'60px', lineHeight:1.45, cursor:'text' }} />
                <div style={{ marginTop:'1.25rem', display:'flex', justifyContent:'center', alignItems:'center', gap:'.75rem' }}
                  onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
                  <span style={{ fontSize:'.8rem', color:text, opacity:.65 }}>Button label:</span>
                  <button
                    onClick={() => onUpdate({ ...config, ctaLabel: (config?.ctaLabel||'shop') === 'shop' ? 'book' : 'shop' })}
                    style={{ background:accent, border:'none', color:'#fff', padding:'.4rem 1.4rem',
                      borderRadius:'20px', fontWeight:'700', fontSize:'.88rem', cursor:'pointer',
                      textTransform:'capitalize', letterSpacing:'.03em', boxShadow:`0 3px 12px ${accent}50` }}>
                    {(config?.ctaLabel||'shop') === 'shop' ? 'Shop' : 'Book'}
                  </button>
                  <span style={{ fontSize:'.72rem', color:text, opacity:.45 }}>click to toggle</span>
                </div>
              </>
            ) : (
              <>
                <div style={{ background:'rgba(0,0,0,0.22)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
                  border:'1px solid rgba(255,255,255,.18)', borderRadius:'16px',
                  padding:'2.2vh 2.8vw' }}>
                  <h1 className="hpt-hero-headline" style={{ fontSize:'clamp(1rem, 2.8vw, 2rem)', fontWeight:'900', margin:'0 0 .25rem',
                    lineHeight:1.08, letterSpacing:'-.03em', color: accent, fontFamily: headingFont,
                    textShadow:`0 2px 16px rgba(0,0,0,.95), 0 0 40px rgba(0,0,0,.6), -1px -1px 0 rgba(0,0,0,.25), 1px -1px 0 rgba(0,0,0,.25)` }}>
                    {config?.headline || shopData?.name || 'Welcome'}
                  </h1>
                  <div style={{ width:'1.5rem', height:'2px', background:`linear-gradient(90deg,${accent},${accent}45)`,
                    borderRadius:'2px', margin:'.35rem auto .6rem' }} />
                  <p className="hpt-hero-subtitle" style={{ fontSize:'clamp(.75rem, 1.4vw, 1rem)', color:'rgba(255,255,255,.95)',
                    lineHeight:1.5, margin:0, animation:'hpt-heroIn .9s cubic-bezier(.4,0,.2,1) .28s both',
                    textShadow:'0 1px 10px rgba(0,0,0,.9)' }}>
                    {config?.subtitle || 'Discover amazing products'}
                  </p>
                  <div style={{ marginTop:'1vh', animation:'hpt-heroIn 1s cubic-bezier(.4,0,.2,1) .5s both' }}>
                    <button
                      className="hpt-hero-cta"
                      onClick={onCtaClick}
                      style={{ background:accent, border:'none', color:'#fff',
                        padding:'.5vh 2vw', borderRadius:'25px',
                        fontWeight:'700', fontSize:'clamp(.75rem, 1.3vw, .9rem)',
                        cursor:'pointer', textTransform:'capitalize', letterSpacing:'.04em',
                        boxShadow:`0 4px 18px ${accent}55`, transition:'all .2s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 8px 28px ${accent}70`; }}
                      onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=`0 4px 18px ${accent}55`; }}
                    >
                      {(config?.ctaLabel||'shop') === 'shop' ? 'Shop' : 'Book'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          )}
        </div>

        {/* Edit controls — under the section (not overlaid on the image), left-aligned,
            sized to match the move-up/down/delete controls at the section's bottom right. */}
        {editable && (() => {
          const isMobile = window.innerWidth <= 768;
          const btnSize = isMobile ? '28px' : '32px';
          const iconSize = isMobile ? 14 : 16;
          const btnBase = {
            border: `1px solid ${accent}`, borderRadius: '6px',
            width: btnSize, height: btnSize, minWidth: btnSize, minHeight: btnSize,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', transition: 'all .2s ease',
          };
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', justifyContent: 'flex-start', padding: '.6rem 0 0' }}>
              <button onClick={() => document.getElementById(`hero-bg-${config?.id}`)?.click()}
                style={{ ...btnBase, background: 'transparent', color: accent }}
                title="Upload media">
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </button>
              {config?.backgroundType !== 'video' && config?.backgroundImage && (
                <button onClick={toggleZoom}
                  style={{ ...btnBase, background: config?.disableZoom ? 'transparent' : accent, color: config?.disableZoom ? accent : '#fff' }}
                  title={config?.disableZoom ? 'Zoom animation off — click to enable' : 'Zoom animation on — click to disable'}>
                  <ZoomIn size={iconSize} />
                </button>
              )}
              {config?.backgroundImage && (
                <button onClick={toggleDrag}
                  style={{ ...btnBase, background: dragMode ? accent : 'transparent', color: dragMode ? '#fff' : accent }}
                  title={dragMode ? 'Save position' : 'Adjust position'}>
                  {dragMode
                    ? <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    : <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>
                  }
                </button>
              )}
              <button onClick={cycleContentVisible}
                style={{ ...btnBase,
                  background: contentMode==='hidden' ? accent : contentMode==='button' ? `${accent}80` : 'transparent',
                  color: contentMode==='hidden' ? '#fff' : accent }}
                title={
                  contentMode==='full' ? 'Showing headline, description & button — click to keep just the button'
                  : contentMode==='button' ? 'Showing just the button — click to hide everything'
                  : 'Everything hidden — click to show headline, description & button'
                }>
                {contentMode==='hidden' ? <EyeOff size={iconSize} /> : <Eye size={iconSize} />}
              </button>
              {showContent && (
                <button onClick={cycleContentPosition}
                  style={{ ...btnBase, background: 'transparent', color: accent }}
                  title={`Text position: ${contentPosition} (click to move)`}>
                  <Move size={iconSize} />
                </button>
              )}
              <input type="file" id={`hero-bg-${config?.id}`} accept="image/*,video/*" style={{ display:'none' }}
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; f && handleFileUpload(f); }} />
            </div>
          );
        })()}
      </SectionWrapper>
    </div>
  );
};

// ─── FEATURED SERVICES SECTION ────────────────────────────────────────────────
// Mirrors the public Shop tab's own Featured card display (FeaturedItem cards +
// the same 1×/2×/3× density toggle) instead of a bespoke grid.
export const FeaturedServicesSection = ({ config, theme, shopData, shopServices, editable, onUpdate, onServiceClick, onOrderClick, onLikeClick, isAuthenticated }) => {
  const featuredSvcs = (shopServices || []).filter(s => !s.deleted && s.isFeatured);
  const fallbackSvcs = (shopServices || []).filter(s => !s.deleted).slice(0, config?.serviceCount || 4);
  const services     = (featuredSvcs.length > 0 ? featuredSvcs : fallbackSvcs).slice(0, config?.serviceCount || 4);

  // In public view, only hide when there's truly nothing to show (no services
  // in the shop at all) — the fallback above already matches what the editor
  // preview shows, so an owner who added this section without explicitly
  // marking anything Featured still sees the same services on the live page.
  if (!editable && services.length === 0) return null;

  const accent  = theme?.colors?.accent   || '#6366f1';
  const surface = theme?.colors?.surface  || '#1a1a2e';
  const text    = theme?.colors?.text     || '#fff';

  const cols = Math.min(config?.columns || 2, Math.max(1, services.length));
  const scrollId = `hero-scroll-home-featured-svc-${config?.id || 'services'}`;

  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) { .hpt-fsvc-outer { padding: clamp(2rem,3.5vw,4rem) clamp(3rem,6vw,6rem); } }
        @media (max-width: 768px) { .hpt-fsvc-outer { padding: clamp(.5rem,1.5vw,1rem) clamp(.75rem,2vw,1.5rem); } }
      `}</style>
      <div className="hpt-fsvc-outer">
        <div className="hpt-wrap">
        <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between',
          marginBottom:'1.5rem', gap:'1rem', flexWrap:'wrap' }}>
          <SectionHead title={config?.title || 'Featured Services'} accent={accent} text={text} alignLeft
            editable={editable} config={config} onUpdate={onUpdate} placeholder="Services section title" />
          <div style={{ display:'flex', gap:'.6rem', alignItems:'center', flexShrink:0 }}>
            {services.length > 0 && (
              <span style={{ fontSize:'.68rem', fontWeight:'700', color:accent, opacity:.75, letterSpacing:'.06em', textTransform:'uppercase' }}>
                {services.length} {services.length===1?'service':'services'}
              </span>
            )}
            {services.length > 1 && (
              <span
                onClick={() => onUpdate({ ...config, columns: (config?.columns||2) === 1 ? 2 : (config?.columns||2) === 2 ? 3 : 1 })}
                title={cols === 1 ? 'Switch to 2 columns' : cols === 2 ? 'Switch to 3 columns' : 'Switch to 1 column'}
                style={{ fontSize:'.75rem', fontWeight:800, display:'inline-flex', alignItems:'center', gap:'.15rem',
                  borderRadius:4, border:`1px solid ${accent}40`, padding:'.25rem .45rem', color:accent, cursor:'pointer', userSelect:'none' }}
              >{cols}×<Grid size={12} /></span>
            )}
            {editable && (
              <select value={config?.serviceCount||4} onChange={e => onUpdate({ ...config, serviceCount:parseInt(e.target.value) })}
                style={{ padding:'.35rem .6rem', borderRadius:'8px', background:surface, color:text,
                  border:`1px solid ${accent}40`, fontSize:'.78rem' }}>
                {[2,3,4,5,6,8].map(n => <option key={n} value={n}>{n} services</option>)}
              </select>
            )}
          </div>
        </div>

        {services.length === 0 ? (
          <div style={{ textAlign:'center', padding:'4rem 2rem', opacity:.5 }}>
            <Store size={52} color={accent} style={{ margin:'0 auto 1rem', opacity:.35, display:'block' }}/>
            <h3 style={{ color:text, margin:'0 0 .4rem', fontSize:'1rem' }}>No featured services yet</h3>
          </div>
        ) : cols === 1 ? (
          <div style={{ position:'relative' }}>
            <HeroScrollContainer id={scrollId} theme={theme}>
              {services.map((s, i) => (
                <FeaturedItem
                  key={s.id}
                  style={{ animationDelay:`${i * 0.07}s` }}
                  item={{ ...s, shopName: shopData?.name, isService:true }}
                  theme={theme}
                  onItemClick={onServiceClick}
                  onOrderClick={onOrderClick}
                  onLikeClick={onLikeClick}
                  isAuthenticated={isAuthenticated}
                  showTopBar={false}
                  compactBottom={true}
                  columns={2}
                  disableZoom={true}
                />
              ))}
            </HeroScrollContainer>
            {services.length > 1 && (() => {
              const btnBase = { position:'absolute', top:'50%', transform:'translateY(-50%)', background:'rgba(0,0,0,0.5)', backdropFilter:'blur(6px)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:'50%', width:'36px', height:'36px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', zIndex:10, color:'#fff', padding:0 };
              return (<>
                <button style={{ ...btnBase, left:'1rem' }} onClick={() => { const el = document.getElementById(scrollId); el?.scrollBy({ left: -el.offsetWidth, behavior:'smooth' }); }}><ChevronLeft size={18} /></button>
                <button style={{ ...btnBase, right:'1rem' }} onClick={() => { const el = document.getElementById(scrollId); el?.scrollBy({ left: el.offsetWidth, behavior:'smooth' }); }}><ChevronRight size={18} /></button>
              </>);
            })()}
          </div>
        ) : (
          <CategoryScrollableGrid
            theme={theme}
            $rows={services.length <= 3 ? 1 : 2}
            $desktopRows={services.length >= 5 ? 2 : 1}
            $mobileCols={cols}
          >
            {services.map(s => (
              <FeaturedItem
                key={s.id}
                item={{ ...s, shopName: shopData?.name, isService:true }}
                theme={theme}
                onItemClick={onServiceClick}
                onOrderClick={onOrderClick}
                onLikeClick={onLikeClick}
                isAuthenticated={isAuthenticated}
                showTopBar={false}
                compactBottom={true}
                columns={cols}
                disableZoom={true}
              />
            ))}
          </CategoryScrollableGrid>
        )}
        </div>{/* /hpt-wrap */}
      </div>{/* /hpt-fsvc-outer */}
    </SectionWrapper>
  );
};

// ─── FAQ SECTION ─────────────────────────────────────────────────────────────
export const FaqSection = ({ config, theme, editable, onUpdate }) => {
  const accent  = theme?.colors?.accent  || '#6366f1';
  const text    = theme?.colors?.text    || '#fff';
  const surface = theme?.colors?.surface || '#1a1a2e';
  const [openIdx, setOpenIdx] = useState(null);

  const items = config?.items || [
    { q: 'What are your hours?', a: 'We are open Monday–Friday 9am–6pm.' },
    { q: 'Do you offer refunds?',  a: 'Yes — full refunds within 30 days of purchase.' },
  ];

  const setItem = (i, field, val) => {
    const next = items.map((it, idx) => idx === i ? { ...it, [field]: val } : it);
    onUpdate?.({ ...config, items: next });
  };
  const addItem    = () => onUpdate?.({ ...config, items: [...items, { q: '', a: '' }] });
  const removeItem = (i) => onUpdate?.({ ...config, items: items.filter((_, idx) => idx !== i) });

  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) {
          .hpt-faq-outer { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); }
          .hpt-faq-cols  { display: grid; grid-template-columns: 1fr 1fr; gap: 0.25rem 2.5rem; align-items: start; }
        }
        @media (max-width: 768px) {
          .hpt-faq-outer { padding: clamp(.5rem,1.5vw,1rem) clamp(.75rem,2vw,1.5rem); }
          .hpt-faq-cols  { display: flex; flex-direction: column; gap: 2px; }
        }
      `}</style>
      <div className="hpt-faq-outer">
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
        <SectionHead title={config?.title || 'FAQ'} accent={accent} text={text} alignLeft
          editable={editable} config={config} onUpdate={onUpdate} placeholder="FAQ section title" />
        <div className="hpt-faq-cols">
          {items.map((item, i) => (
            <div key={i} style={{ borderRadius:12, overflow:'hidden',
              border:`1px solid ${accent}28`, background:`${surface}60` }}>
              {/* Question row */}
              <div style={{ display:'flex', alignItems:'center', gap:'0.75rem',
                padding:'0.85rem 1rem', cursor:'pointer', userSelect:'none' }}
                onClick={() => setOpenIdx(openIdx === i ? null : i)}>
                {editable ? (
                  <input value={item.q} placeholder="Question…"
                    onChange={e => { e.stopPropagation(); setItem(i, 'q', e.target.value); }}
                    onClick={e => e.stopPropagation()}
                    style={{ flex:1, background:'transparent', border:'none', outline:'none',
                      color:text, fontSize:'0.95rem', fontWeight:600 }} />
                ) : (
                  <span style={{ flex:1, fontSize:'0.95rem', fontWeight:600, color:text }}>{item.q}</span>
                )}
                <ChevronDown size={18} color={accent} style={{ flexShrink:0,
                  transform: openIdx === i ? 'rotate(180deg)' : 'none', transition:'transform .2s' }} />
                {editable && (
                  <button onClick={e => { e.stopPropagation(); removeItem(i); }}
                    style={{ background:'none', border:'none', color:`${text}55`, cursor:'pointer', padding:2, flexShrink:0, display:'flex' }}>
                    <Minus size={15} />
                  </button>
                )}
              </div>
              {/* Answer — expand/collapse */}
              {openIdx === i && (
                <div style={{ padding:'0 1rem 0.85rem', borderTop:`1px solid ${accent}18` }}>
                  {editable ? (
                    <textarea value={item.a} placeholder="Answer…"
                      onChange={e => setItem(i, 'a', e.target.value)}
                      rows={3}
                      style={{ width:'100%', background:'transparent', border:'none', outline:'none',
                        color:`${text}cc`, fontSize:'0.88rem', lineHeight:1.55, resize:'vertical',
                        fontFamily:'inherit', marginTop:'0.6rem', boxSizing:'border-box' }} />
                  ) : (
                    <p style={{ margin:'0.6rem 0 0', color:`${text}cc`, fontSize:'0.88rem', lineHeight:1.55 }}>{item.a}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {editable && (
          <button onClick={addItem} style={{ marginTop:'0.75rem', display:'flex', alignItems:'center',
            gap:'0.4rem', background:`${accent}18`, border:`1px dashed ${accent}50`, borderRadius:10,
            padding:'0.5rem 1rem', color:accent, fontSize:'0.82rem', fontWeight:700, cursor:'pointer', width:'100%',
            justifyContent:'center' }}>
            <Plus size={15} /> Add Question
          </button>
        )}
        </div>{/* /maxWidth wrap */}
      </div>{/* /hpt-faq-outer */}
    </SectionWrapper>
  );
};

// ─── REVIEWS SECTION ──────────────────────────────────────────────────────────
export const ReviewsSection = ({ config, theme, editable, onUpdate }) => {
  const accent  = theme?.colors?.accent  || '#6366f1';
  const text    = theme?.colors?.text    || '#fff';
  const surface = theme?.colors?.surface || '#1a1a2e';

  const reviews = config?.reviews || [];

  const setReview = (i, field, val) => {
    const next = reviews.map((r, idx) => idx === i ? { ...r, [field]: val } : r);
    onUpdate?.({ ...config, reviews: next });
  };
  const addReview    = () => onUpdate?.({ ...config, reviews: [...reviews, { name:'', rating:5, body:'', date:'' }] });
  const removeReview = (i) => onUpdate?.({ ...config, reviews: reviews.filter((_, idx) => idx !== i) });

  const avg = reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 5), 0) / reviews.length).toFixed(1) : null;

  const StarRow = ({ rating, onChange }) => (
    <div style={{ display:'flex', gap:2 }}>
      {[1,2,3,4,5].map(n => (
        <Star key={n} size={14}
          fill={n <= rating ? accent : 'none'}
          color={n <= rating ? accent : `${text}40`}
          style={{ cursor: onChange ? 'pointer' : 'default' }}
          onClick={() => onChange?.(n)} />
      ))}
    </div>
  );

  return (
    <SectionWrapper>
      <style>{`
        @media (min-width: 769px) {
          .hpt-rev-outer { padding: clamp(2.5rem,4vw,4.5rem) clamp(3rem,6vw,6rem); }
          .hpt-rev-layout { display: grid; grid-template-columns: 220px 1fr; gap: 3rem; align-items: start; }
          .hpt-rev-sidebar { position: sticky; top: 2rem; }
          .hpt-rev-mobile-add { display: none !important; }
        }
        @media (max-width: 768px) {
          .hpt-rev-outer { padding: clamp(.5rem,1.5vw,1rem) clamp(.75rem,2vw,1.5rem); }
          .hpt-rev-layout { display: block; }
          .hpt-rev-sidebar { margin-bottom: 1.25rem; }
          .hpt-rev-sidebar button { display: none !important; }
        }
      `}</style>
      <div className="hpt-rev-outer">
        <div style={{ maxWidth:1320, margin:'0 auto' }}>
          <SectionHead title={config?.title || 'Reviews'} accent={accent} text={text} alignLeft
            editable={editable} config={config} onUpdate={onUpdate} placeholder="Reviews section title" />

          <div className="hpt-rev-layout">
            {/* ── Sidebar: big rating summary ── */}
            <div className="hpt-rev-sidebar">
              {avg ? (
                <div style={{ background:`${surface}60`, border:`1px solid ${accent}28`,
                  borderRadius:16, padding:'1.5rem', textAlign:'center' }}>
                  <div style={{ fontSize:'3.5rem', fontWeight:800, color:accent, lineHeight:1, marginBottom:'.25rem' }}>{avg}</div>
                  <StarRow rating={Math.round(parseFloat(avg))} />
                  <div style={{ fontSize:'0.78rem', color:`${text}60`, marginTop:'.5rem' }}>
                    {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{ marginTop:'1rem', display:'flex', flexDirection:'column', gap:'.3rem' }}>
                    {[5,4,3,2,1].map(n => {
                      const count = reviews.filter(r => Math.round(r.rating||5) === n).length;
                      const pct = reviews.length ? (count / reviews.length) * 100 : 0;
                      return (
                        <div key={n} style={{ display:'flex', alignItems:'center', gap:'.4rem', fontSize:'.72rem' }}>
                          <span style={{ color:`${text}60`, width:8, textAlign:'right' }}>{n}</span>
                          <Star size={10} fill={accent} color={accent} />
                          <div style={{ flex:1, height:4, borderRadius:4, background:`${accent}18`, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${pct}%`, background:accent, borderRadius:4, transition:'width .4s ease' }} />
                          </div>
                          <span style={{ color:`${text}50`, width:16, textAlign:'right' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{ background:`${surface}60`, border:`1px solid ${accent}28`,
                  borderRadius:16, padding:'1.5rem', textAlign:'center', opacity:.5 }}>
                  <Star size={32} color={accent} style={{ margin:'0 auto .5rem', display:'block' }} />
                  <div style={{ fontSize:'.85rem', color:text }}>No reviews yet</div>
                </div>
              )}
              {editable && (
                <button onClick={addReview} style={{ marginTop:'0.75rem', display:'flex', alignItems:'center',
                  gap:'0.4rem', background:`${accent}18`, border:`1px dashed ${accent}50`, borderRadius:10,
                  padding:'0.5rem 1rem', color:accent, fontSize:'0.82rem', fontWeight:700, cursor:'pointer', width:'100%',
                  justifyContent:'center' }}>
                  <Plus size={15} /> Add Review
                </button>
              )}
            </div>

            {/* ── Review cards ── */}
            <div>
              {reviews.length > 0 ? (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:'0.75rem' }}>
                  {reviews.map((r, i) => (
                    <div key={i} style={{ borderRadius:14, padding:'0.85rem 1rem', position:'relative',
                      background:`${surface}70`, border:`1px solid ${accent}28` }}>
                      {editable && (
                        <button onClick={() => removeReview(i)}
                          style={{ position:'absolute', top:8, right:8, background:'none', border:'none',
                            color:`${text}45`, cursor:'pointer', display:'flex', padding:2 }}>
                          <Minus size={13} />
                        </button>
                      )}
                      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.5rem' }}>
                        <div style={{ width:32, height:32, borderRadius:'50%', background:`${accent}30`,
                          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                          fontSize:'0.9rem', fontWeight:700, color:accent }}>
                          {(r.name || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          {editable ? (
                            <input value={r.name} placeholder="Reviewer name"
                              onChange={e => setReview(i, 'name', e.target.value)}
                              style={{ background:'transparent', border:'none', outline:'none',
                                color:text, fontSize:'0.85rem', fontWeight:700, width:'100%' }} />
                          ) : (
                            <span style={{ fontSize:'0.85rem', fontWeight:700, color:text }}>{r.name}</span>
                          )}
                          {r.date && <span style={{ fontSize:'0.7rem', color:`${text}50`, display:'block' }}>{r.date}</span>}
                        </div>
                      </div>
                      <StarRow rating={r.rating || 5} onChange={editable ? (n) => setReview(i, 'rating', n) : null} />
                      {editable ? (
                        <textarea value={r.body} placeholder="Review text…" rows={3}
                          onChange={e => setReview(i, 'body', e.target.value)}
                          style={{ marginTop:'0.5rem', width:'100%', background:'transparent', border:'none',
                            outline:'none', color:`${text}cc`, fontSize:'0.84rem', lineHeight:1.5,
                            resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }} />
                      ) : (
                        <p style={{ margin:'0.5rem 0 0', color:`${text}cc`, fontSize:'0.84rem', lineHeight:1.5 }}>{r.body}</p>
                      )}
                      <div style={{ marginTop:'0.6rem', display:'flex', alignItems:'center', gap:'0.3rem',
                        color:`${text}40`, fontSize:'0.72rem' }}>
                        <ThumbsUp size={11} /> Helpful
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !editable && <div style={{ padding:'3rem 2rem', textAlign:'center', opacity:.4, color:text, fontSize:'.9rem' }}>No reviews yet</div>
              )}
              {/* Mobile add button (sidebar has it on desktop) */}
              {editable && (
                <button onClick={addReview} style={{ marginTop:'0.75rem', display:'flex', alignItems:'center',
                  gap:'0.4rem', background:`${accent}18`, border:`1px dashed ${accent}50`, borderRadius:10,
                  padding:'0.5rem 1rem', color:accent, fontSize:'0.82rem', fontWeight:700, cursor:'pointer', width:'100%',
                  justifyContent:'center' }} className="hpt-rev-mobile-add">
                  <Plus size={15} /> Add Manual Review
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

// ─── TEMPLATE COMPONENTS ──────────────────────────────────────────────────────
const buildSectionRenderer = (shopData, theme, editable, onUpdateSection, onItemClick, onCtaClick, extra = {}) => (section) => {
  const { onOrderClick, onLikeClick, isAuthenticated } = extra;
  const props = {
    config:   section.config,
    theme,
    shopData,
    shopItems:    shopData?.items,
    shopServices: shopData?.services,
    editable,
    onUpdate: (cfg) => onUpdateSection?.(section.id, cfg),
    onItemClick,
    onServiceClick: onItemClick,
    onCtaClick,
    onOrderClick,
    onLikeClick,
    isAuthenticated,
  };
  const map = {
    'hero-banner':       HeroBannerSection,
    'featured-items':    FeaturedItemsSection,
    'featured-services': FeaturedServicesSection,
    'photo-gallery':     PhotoGallerySection,
    'featured-video':    FeaturedVideoSection,
    'mission-statement': MissionStatementSection,
    'calendar-events':   CalendarEventsSection,
    'services-grid':     ServicesGridSection,
    'contact':           ContactSection,
    'text-block':        TextBlockSection,
    'map':               MapSection,
    'faq':               FaqSection,
    'reviews':           ReviewsSection,
  };
  const Comp = map[section.type];
  return Comp ? <Comp key={section.id} {...props} /> : null;
};

export const StreetwearTemplate = ({ shopData, theme, sections, editable, onUpdateSection, onItemClick, onCtaClick, onOrderClick, onLikeClick, isAuthenticated }) => {
  const render = buildSectionRenderer(shopData, theme, editable, onUpdateSection, onItemClick, onCtaClick, { onOrderClick, onLikeClick, isAuthenticated });
  return (
    <StreetwearFrame theme={theme}>
      {sections?.map((s, i) => (
        <React.Fragment key={s.id}>
          {render(s)}
          {i < sections.length-1 && <StreetwearDivider theme={theme}/>}
        </React.Fragment>
      ))}
    </StreetwearFrame>
  );
};

export const OrganizationTemplate = ({ shopData, theme, sections, editable, onUpdateSection, onItemClick, onCtaClick, onOrderClick, onLikeClick, isAuthenticated }) => {
  const render = buildSectionRenderer(shopData, theme, editable, onUpdateSection, onItemClick, onCtaClick, { onOrderClick, onLikeClick, isAuthenticated });
  return (
    <OrganizationFrame theme={theme}>
      {sections?.map((s, i) => (
        <React.Fragment key={s.id}>
          {render(s)}
          {i < sections.length-1 && <OrganizationDivider theme={theme}/>}
        </React.Fragment>
      ))}
    </OrganizationFrame>
  );
};

export const TechTemplate = ({ shopData, theme, sections, editable, onUpdateSection, onItemClick, onCtaClick, onOrderClick, onLikeClick, isAuthenticated }) => {
  const render = buildSectionRenderer(shopData, theme, editable, onUpdateSection, onItemClick, onCtaClick, { onOrderClick, onLikeClick, isAuthenticated });
  return (
    <TechFrame theme={theme}>
      {sections?.map((s, i) => (
        <React.Fragment key={s.id}>
          {render(s)}
          {i < sections.length-1 && <TechDivider theme={theme}/>}
        </React.Fragment>
      ))}
    </TechFrame>
  );
};

export const MinimalistTemplate = ({ shopData, theme, sections, editable, onUpdateSection, onItemClick, onCtaClick, onOrderClick, onLikeClick, isAuthenticated }) => {
  const render = buildSectionRenderer(shopData, theme, editable, onUpdateSection, onItemClick, onCtaClick, { onOrderClick, onLikeClick, isAuthenticated });
  return (
    <MinimalistFrame theme={theme}>
      {sections?.map((s, i) => (
        <React.Fragment key={s.id}>
          {render(s)}
          {i < sections.length-1 && <MinimalistDivider theme={theme}/>}
        </React.Fragment>
      ))}
    </MinimalistFrame>
  );
};

export const LocalMarketTemplate = ({ shopData, theme, sections, editable, onUpdateSection, onItemClick, onCtaClick, onOrderClick, onLikeClick, isAuthenticated }) => {
  const render = buildSectionRenderer(shopData, theme, editable, onUpdateSection, onItemClick, onCtaClick, { onOrderClick, onLikeClick, isAuthenticated });
  return (
    <LocalMarketFrame theme={theme}>
      {sections?.map((s, i) => (
        <React.Fragment key={s.id}>
          {render(s)}
          {i < sections.length-1 && <LocalMarketDivider theme={theme}/>}
        </React.Fragment>
      ))}
    </LocalMarketFrame>
  );
};

// ─── SECTION TYPES ────────────────────────────────────────────────────────────
export const SECTION_TYPES = [
  { value:'hero-banner',       label:'Hero Banner',        icon:'🎯' },
  { value:'featured-items',    label:'Featured Items',     icon:'⭐' },
  { value:'featured-services', label:'Featured Services',  icon:'🔧' },
  { value:'photo-gallery',     label:'Photo Gallery',      icon:'📸' },
  { value:'featured-video',    label:'Featured Video',     icon:'🎥' },
  { value:'mission-statement', label:'Mission Statement',  icon:'💡' },
  { value:'calendar-events',   label:'Calendar / Events',  icon:'📅' },
  { value:'services-grid',     label:'Services Grid',      icon:'🎁' },
  { value:'contact',           label:'Contact Info',       icon:'📧' },
  { value:'text-block',        label:'Text Block',         icon:'📝' },
  { value:'map',               label:'Map',                icon:'🗺️' },
  { value:'faq',               label:'FAQ',                icon:'❓' },
  { value:'reviews',           label:'Reviews',            icon:'⭐' },
];
